import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import { getAuth, signOut } from "firebase/auth";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../component/AuthProvider";
import { fullMenu } from "../dashboard/layout"; // Project-ийн замтай тааруулна уу.

import {
  FiCheck,
  FiCalendar,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiCreditCard,
  FiDollarSign,
  FiEdit3,
  FiKey,
  FiLoader,
  FiLock,
  FiLogOut,
  FiMapPin,
  FiMinus,
  FiPlus,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiSettings,
  FiShield,
  FiStar,
  FiUsers,
  FiX,
  FiZap,
} from "react-icons/fi";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const Toast = MySwal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 2400,
  timerProgressBar: true,
  customClass: {
    popup:
      "tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/95 tw-px-4 tw-py-3 tw-shadow-2xl tw-shadow-slate-900/10 tw-backdrop-blur-xl",
    title: "tw-m-0 tw-text-sm tw-font-semibold tw-text-slate-800",
    timerProgressBar: "tw-bg-blue-600",
  },
});

const NUMBER_FIELDS = new Set([
  "HOURLY_RATE",
  "PAYMENT_GRACE_MINUTES",
  "PRICE_PER_HOUR",
  "INVITED_FREE_MINUTES",
  "INVITED_FREE_MINUTES",
  "lat",
  "lng",
  "prepaidHours",
  "priceParking",
  "radius",
  "buttonRemainValue",
  "guestPlateDailyLimit",
  "invitationNumerTime",
  "inviteCodeDailyLimit",
]);

const NON_NEGATIVE_FIELDS = [
  "HOURLY_RATE",
  "PAYMENT_GRACE_MINUTES",
  "PRICE_PER_HOUR",
  "prepaidHours",
  "priceParking",
  "radius",
  "buttonRemainValue",
  "guestPlateDailyLimit",
  "invitationNumerTime",
  "inviteCodeDailyLimit",
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PARKING_ID_PATTERN = /^[A-Za-z0-9_-]{3,64}$/;

function normalizeEmail(value = "") {
  return String(value).trim().toLowerCase();
}

function normalizeRole(value = "") {
  return String(value).trim().toLowerCase();
}

function normalizeBoolean(value, fallback = false) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;

  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  if (normalized === "true") return true;
  if (normalized === "false") return false;

  return Boolean(fallback);
}

function isFiniteNonNegative(value) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue >= 0;
}

function validateParkingConfig(data) {
  const errors = [];
  const name = String(data.root.name || data.config.name || "").trim();

  if (!name) errors.push("Зогсоолын нэр оруулна уу.");

  NON_NEGATIVE_FIELDS.forEach((field) => {
    if (!isFiniteNonNegative(data.config[field])) {
      errors.push(`${field} утгыг 0 эсвэл түүнээс их тоогоор оруулна уу.`);
    }
  });

  if (!isFiniteNonNegative(data.config.FREE_MINUTES)) {
    errors.push("FREE_MINUTES утгыг зөв оруулна уу.");
  }

  if (!isFiniteNonNegative(data.config.INVITED_FREE_MINUTES)) {
    errors.push("INVITED_FREE_MINUTES утгыг зөв оруулна уу.");
  } else if (Number(data.config.INVITED_FREE_MINUTES) > 7 * 24 * 60) {
    errors.push("INVITED_FREE_MINUTES 0–10080 минутын хооронд байна.");
  }

  if (!isFiniteNonNegative(data.config.FIXED_PRICE)) {
    errors.push("FIXED_PRICE утгыг зөв оруулна уу.");
  }

  if (!isFiniteNonNegative(data.config.CONFIG_PRICE_LARGE)) {
    errors.push("CONFIG_PRICE_LARGE буюу том машины цагийн үнийг зөв оруулна уу.");
  }

  if (!isFiniteNonNegative(data.config.FIXED_PRICE_LARGE)) {
    errors.push("FIXED_PRICE_LARGE буюу том машины тогтмол үнийг зөв оруулна уу.");
  }

  const lat = Number(data.config.lat);
  const lng = Number(data.config.lng);

  if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
    errors.push("Өргөрөг (lat) -90–90 хооронд байна.");
  }

  if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
    errors.push("Уртраг (lng) -180–180 хооронд байна.");
  }

  return errors;
}

const createDefaultData = () => ({
  root: {
    name: "",
    joinCode: "",
  },
  config: {
    CONFIG_PRICE_LARGE: "5000",
    FIXED_PRICE: "5000",
    FIXED_PRICE_LARGE: "5000",
    FREE_MINUTES: "60",
    INVITED_FREE_MINUTES: 240,
    HOURLY_RATE: 1000,
    PAYMENT_GRACE_MINUTES: 13,
    PAYMENT_TYPE: "hourly",
    PRICE_PER_HOUR: 1000,
    address: "ХУД",
    apiPass: "",
    apiUser: "",
    buttonRemain: true,
    buttonRemainValue: 5,
    chargeStrategy: "subtract",
    ebarimt: false,
    guestPlateDailyLimit: 20,
    invitationNumerTime: 6,
    inviteCodeDailyLimit: 10,
    inviteNumber: true,
    isMockMode: false,
    joinCode: "515151",
    lat: 47.922054,
    lng: 106.915741,
    merchantCode: "",
    name: "",
    note: "Тавтай морил.",
    prepaidHours: 1,
    priceParking: 1000,
    radius: 100,
    status: "normal",
    vipNote: "Тавтай морил.",
  },
  ebarimt: { terminalNo: "" },
  gate: {
    enterOpen: false,
    exitOpen: false,
    joinCode: "",
    cameraPlan: {
      enterCount: 1,
      exitCount: 1,
      totalCount: 2,
    },
    // services/remote_config.py publishes hardware cameras here.
    // Super Admin does NOT overwrite this registry.
    cameras: {},
    // Dashboard-only presentation settings. Safe from remote_config overwrite.
    dashboardButtons: {},
  },
});

const createNewParkingForm = () => ({
  parkingId: "",
  name: "",
  joinCode: "",
  address: "",
  freeMinutes: "60",
  invitedFreeMinutes: "240",
  hourlyPrice: "1000",
  largeVehiclePrice: "5000",
  fixedLargeVehiclePrice: "5000",
  enterCameraCount: "1",
  exitCameraCount: "1",
  managerEmail: "",
});

function normalizeGateCameraRegistry(rawValue) {
  const rows = Array.isArray(rawValue)
    ? rawValue
    : rawValue && typeof rawValue === "object"
      ? Object.entries(rawValue).map(([key, value]) => ({
          ...(value && typeof value === "object" ? value : {}),
          camera_id: value?.camera_id || value?.id || key,
        }))
      : [];

  const seen = new Set();

  return rows
    .map((camera) => {
      const cameraId = String(
        camera?.camera_id || camera?.id || ""
      )
        .trim()
        .toLowerCase();

      const role =
        String(camera?.role || "")
          .trim()
          .toLowerCase() === "exit"
          ? "exit"
          : "enter";

      return {
        camera_id: cameraId,
        name: String(
          camera?.name ||
            (role === "exit" ? "Гарах хаалга" : "Орох хаалга")
        ).trim(),
        role,
        vendor: String(camera?.vendor || "")
          .trim()
          .toLowerCase(),
        enabled: camera?.enabled !== false,
        configured:
          camera?.configured !== false,
      };
    })
    .filter((camera) => {
      if (!camera.camera_id || seen.has(camera.camera_id)) return false;
      seen.add(camera.camera_id);
      return true;
    });
}

function normalizeCameraPlan(rawPlan, rawCameras) {
  const cameraRows = normalizeGateCameraRegistry(rawCameras);

  const actualEnterCount = cameraRows.filter(
    (camera) =>
      camera.role === "enter" &&
      camera.configured !== false
  ).length;

  const actualExitCount = cameraRows.filter(
    (camera) =>
      camera.role === "exit" &&
      camera.configured !== false
  ).length;

  const hasPlan =
    rawPlan &&
    typeof rawPlan === "object" &&
    !Array.isArray(rawPlan);

  let enterCount = hasPlan
    ? Number(rawPlan.enterCount)
    : actualEnterCount;

  let exitCount = hasPlan
    ? Number(rawPlan.exitCount)
    : actualExitCount;

  if (!Number.isInteger(enterCount) || enterCount < 0) {
    enterCount = actualEnterCount;
  }

  if (!Number.isInteger(exitCount) || exitCount < 0) {
    exitCount = actualExitCount;
  }

  enterCount = Math.min(32, Math.max(0, enterCount));
  exitCount = Math.min(32, Math.max(0, exitCount));

  // Legacy parking document-д cameraPlan/registry хоёулаа байхгүй бол
  // хуучин 1 ENTER + 1 EXIT загварыг хадгална.
  if (
    !hasPlan &&
    actualEnterCount === 0 &&
    actualExitCount === 0
  ) {
    enterCount = 1;
    exitCount = 1;
  }

  return {
    enterCount,
    exitCount,
    totalCount: enterCount + exitCount,
  };
}

function buildPlannedCameraRows(cameraPlan, rawCameras) {
  const plan = normalizeCameraPlan(
    cameraPlan,
    rawCameras
  );

  const actualRows = normalizeGateCameraRegistry(
    rawCameras
  );

  const actualById = new Map(
    actualRows.map((camera) => [
      camera.camera_id,
      camera,
    ])
  );

  const result = [];
  let order = 1;

  const appendRole = (role, count) => {
    for (let index = 1; index <= count; index += 1) {
      const cameraId = `${role}_${index}`;
      const actual = actualById.get(cameraId);

      result.push({
        ...(actual || {}),
        camera_id: cameraId,
        name:
          actual?.name ||
          (role === "enter"
            ? `Орох хаалга ${index}`
            : `Гарах хаалга ${index}`),
        role,
        vendor: actual?.vendor || "",
        enabled:
          actual?.enabled === true,
        configured:
          Boolean(actual) &&
          actual?.configured !== false,
        planned: true,
        order,
      });

      order += 1;
    }
  };

  appendRole("enter", plan.enterCount);
  appendRole("exit", plan.exitCount);

  return result;
}

function reconcileDashboardButtons(
  currentButtons,
  cameraPlan,
  rawCameras
) {
  const normalizedButtons =
    normalizeDashboardButtons(
      currentButtons || {}
    );

  const plannedRows = buildPlannedCameraRows(
    cameraPlan,
    rawCameras
  );

  const plannedIds = new Set(
    plannedRows.map((camera) => camera.camera_id)
  );

  const nextButtons = {
    ...normalizedButtons,
  };

  plannedRows.forEach((camera, index) => {
    const current =
      normalizedButtons[camera.camera_id];

    nextButtons[camera.camera_id] = {
      cameraId: camera.camera_id,
      label:
        String(current?.label || "").trim() ||
        camera.name ||
        camera.camera_id,
      visible:
        current?.visible !== false,
      order:
        Number.isFinite(Number(current?.order))
          ? Math.max(
              0,
              Math.trunc(Number(current.order))
            )
          : index + 1,
    };
  });

  // Plan-аас хасагдсан боловч local registry дээр одоо хүртэл байгаа
  // physical camera Dashboard дээр дахин default-visible болохоос хамгаална.
  Object.keys(nextButtons).forEach((cameraId) => {
    if (plannedIds.has(cameraId)) return;

    nextButtons[cameraId] = {
      ...nextButtons[cameraId],
      cameraId,
      visible: false,
    };
  });

  return nextButtons;
}

function normalizeDashboardButtons(rawValue = {}) {
  if (!rawValue || typeof rawValue !== "object" || Array.isArray(rawValue)) {
    return {};
  }

  const result = {};

  Object.entries(rawValue).forEach(([key, value], index) => {
    if (!value || typeof value !== "object") return;

    const cameraId = String(
      value.cameraId || value.camera_id || key || ""
    )
      .trim()
      .toLowerCase();

    if (!cameraId) return;

    const parsedOrder = Number(value.order);

    result[cameraId] = {
      cameraId,
      label: String(value.label || "").trim().slice(0, 80),
      visible: value.visible !== false,
      order:
        Number.isFinite(parsedOrder) && parsedOrder >= 0
          ? Math.trunc(parsedOrder)
          : index + 1,
    };
  });

  return result;
}

function createDashboardGateRequestId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function buildInitialGateCameraRegistry(
  enterCameraCount,
  exitCameraCount
) {
  const registry = {};
  let order = 1;

  for (let index = 1; index <= enterCameraCount; index += 1) {
    const cameraId = `enter_${index}`;

    registry[cameraId] = {
      camera_id: cameraId,
      name: `Орох хаалга ${index}`,
      role: "enter",
      vendor: "",
      enabled: false,
      configured: false,
      source: "createParking",
      order,
    };

    order += 1;
  }

  for (let index = 1; index <= exitCameraCount; index += 1) {
    const cameraId = `exit_${index}`;

    registry[cameraId] = {
      camera_id: cameraId,
      name: `Гарах хаалга ${index}`,
      role: "exit",
      vendor: "",
      enabled: false,
      configured: false,
      source: "createParking",
      order,
    };

    order += 1;
  }

  return registry;
}

function buildInitialDashboardButtons(cameraRegistry = {}) {
  const buttons = {};

  Object.values(cameraRegistry)
    .sort(
      (a, b) =>
        Number(a?.order || 0) -
        Number(b?.order || 0)
    )
    .forEach((camera, index) => {
      const cameraId = String(
        camera?.camera_id || ""
      )
        .trim()
        .toLowerCase();

      if (!cameraId) return;

      buttons[cameraId] = {
        cameraId,
        label: String(
          camera?.name || cameraId
        ).trim(),
        visible: true,
        order: index + 1,
      };
    });

  return buttons;
}

function classNames(...values) {
  return values.filter(Boolean).join(" ");
}

function toDateInputValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toSafeDate(value) {
  if (!value) return null;
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

function formatCurrency(value) {
  const amount = Number(value);
  return `${Number.isFinite(amount) ? Math.round(amount).toLocaleString() : "0"}₮`;
}

function normalizeParkingData(fetched = {}) {
  const defaults = createDefaultData();
  const fetchedConfig = fetched.config || {};

  return {
    root: {
      name: fetched.name || fetchedConfig.name || "",
      joinCode: fetched.joinCode || "",
    },
    config: {
      ...defaults.config,
      ...fetchedConfig,
      ebarimt: normalizeBoolean(
        fetchedConfig.ebarimt,
        defaults.config.ebarimt
      ),
    },
    ebarimt: {
      ...defaults.ebarimt,
      terminalNo: String(fetched.ebarimt?.terminalNo || ""),
    },
    gate: {
      ...defaults.gate,
      ...(fetched.gate || {}),
      cameras: fetched.gate?.cameras || {},
      cameraPlan: normalizeCameraPlan(
        fetched.gate?.cameraPlan,
        fetched.gate?.cameras
      ),
      dashboardButtons: reconcileDashboardButtons(
        fetched.gate?.dashboardButtons || {},
        fetched.gate?.cameraPlan,
        fetched.gate?.cameras
      ),
    },
  };
}

function flattenMenuItems(items = []) {
  const result = [];

  items.forEach((item) => {
    if (Array.isArray(item.subItems) && item.subItems.length > 0) {
      item.subItems.forEach((subItem) => {
        if (subItem?.url) {
          result.push({
            url: subItem.url,
            label: subItem.label || subItem.url,
            group: item.label || "Бусад",
          });
        }
      });
      return;
    }

    if (item?.url) {
      result.push({
        url: item.url,
        label: item.label || item.url,
        group: "Үндсэн цэс",
      });
    }
  });

  return result;
}

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  hint = "",
  disabled = false,
  autoComplete = "off",
}) {
  return (
    <label className="tw-block">
      <span className="tw-mb-1.5 tw-block tw-text-[12px] tw-font-semibold tw-text-slate-600">
        {label}
      </span>
      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        className="tw-h-11 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-text-sm tw-font-medium tw-text-slate-900 tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)] tw-outline-none tw-transition-all placeholder:tw-text-slate-400 focus:tw-border-blue-400 focus:tw-ring-4 focus:tw-ring-blue-500/10 disabled:tw-cursor-not-allowed disabled:tw-bg-slate-100 disabled:tw-text-slate-500"
      />
      {hint && (
        <span className="tw-mt-1 tw-block tw-text-[11px] tw-leading-4 tw-text-slate-400">
          {hint}
        </span>
      )}
    </label>
  );
}

function SelectField({ label, name, value, onChange, children, hint = "" }) {
  return (
    <label className="tw-block">
      <span className="tw-mb-1.5 tw-block tw-text-[12px] tw-font-semibold tw-text-slate-600">
        {label}
      </span>
      <div className="tw-relative">
        <select
          name={name}
          value={value ?? ""}
          onChange={onChange}
          className="tw-h-11 tw-w-full tw-appearance-none tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-pr-10 tw-text-sm tw-font-medium tw-text-slate-900 tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)] tw-outline-none tw-transition-all focus:tw-border-blue-400 focus:tw-ring-4 focus:tw-ring-blue-500/10"
        >
          {children}
        </select>
        <FiChevronDown className="tw-pointer-events-none tw-absolute tw-right-3.5 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" />
      </div>
      {hint && (
        <span className="tw-mt-1 tw-block tw-text-[11px] tw-leading-4 tw-text-slate-400">
          {hint}
        </span>
      )}
    </label>
  );
}

function Toggle({ label, description, checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      aria-pressed={checked}
      className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50/70 tw-p-4 tw-text-left tw-transition-all hover:tw-border-slate-300 hover:tw-bg-white hover:tw-shadow-sm disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
    >
      <span>
        <span className="tw-block tw-text-sm tw-font-semibold tw-text-slate-800">
          {label}
        </span>
        {description && (
          <span className="tw-mt-0.5 tw-block tw-text-[11px] tw-leading-4 tw-text-slate-400">
            {description}
          </span>
        )}
      </span>
      <span
        className={classNames(
          "tw-relative tw-h-6 tw-w-11 tw-shrink-0 tw-rounded-full tw-transition",
          checked ? "tw-bg-blue-600" : "tw-bg-slate-300"
        )}
      >
        <span
          className={classNames(
            "tw-absolute tw-top-1 tw-h-4 tw-w-4 tw-rounded-full tw-bg-white tw-shadow tw-transition",
            checked ? "tw-left-6" : "tw-left-1"
          )}
        />
      </span>
    </button>
  );
}

function Card({ title, description, icon: Icon, children, action = null }) {
  return (
    <section className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white tw-shadow-[0_1px_2px_rgba(15,23,42,0.03),0_8px_24px_rgba(15,23,42,0.025)]">
      <header className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-border-b tw-border-slate-100 tw-px-4 tw-py-4 sm:tw-px-5 sm:tw-py-5">
        <div className="tw-flex tw-min-w-0 tw-items-start tw-gap-3">
          {Icon && (
            <span className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-blue-50 tw-text-blue-600 tw-ring-1 tw-ring-blue-100">
              <Icon size={17} />
            </span>
          )}
          <div className="tw-min-w-0">
            <h2 className="tw-m-0 tw-text-sm tw-font-bold tw-tracking-[-0.01em] tw-text-slate-900 sm:tw-text-[15px]">
              {title}
            </h2>
            {description && (
              <p className="tw-m-0 tw-mt-1 tw-text-xs tw-leading-5 tw-text-slate-500">
                {description}
              </p>
            )}
          </div>
        </div>
        {action}
      </header>
      <div className="tw-p-4 sm:tw-p-5">{children}</div>
    </section>
  );
}

function EmptyParking() {
  return (
    <div className="tw-flex tw-min-h-[300px] tw-flex-col tw-items-center tw-justify-center tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-300 tw-bg-white tw-p-6 tw-text-center tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
      <span className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-blue-50 tw-text-blue-600 tw-ring-1 tw-ring-blue-100">
        <FiSettings size={24} />
      </span>
      <h2 className="tw-m-0 tw-mt-4 tw-text-base tw-font-bold tw-text-slate-800">
        Зогсоол сонгоогүй байна
      </h2>
      <p className="tw-m-0 tw-mt-2 tw-max-w-sm tw-text-sm tw-leading-6 tw-text-slate-400">
        “Зогсоол” хэсгээс засварлах зогсоолоо сонгох эсвэл шинээр үүсгэнэ үү.
      </p>
    </div>
  );
}

function Modal({ open, title, description, onClose, children }) {
  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-[100000] tw-flex tw-items-end tw-justify-center tw-bg-slate-950/45 tw-p-0 tw-backdrop-blur-[3px] sm:tw-items-center sm:tw-p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label="Хаах"
        className="tw-absolute tw-inset-0"
        onClick={onClose}
      />
      <div className="tw-relative tw-z-10 tw-max-h-[92vh] tw-w-full tw-max-w-2xl tw-overflow-y-auto tw-rounded-t-[24px] tw-border tw-border-slate-200/80 tw-bg-white tw-shadow-2xl tw-shadow-slate-950/15 sm:tw-rounded-[24px]">
        <header className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-border-slate-100 tw-bg-white/95 tw-px-4 tw-py-4 tw-backdrop-blur-xl sm:tw-px-5">
          <div>
            <h2 className="tw-m-0 tw-text-base tw-font-bold tw-text-slate-900">
              {title}
            </h2>
            {description && (
              <p className="tw-m-0 tw-mt-1 tw-text-xs tw-leading-5 tw-text-slate-400">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Цонх хаах"
            className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-xl tw-text-slate-400 tw-transition hover:tw-bg-slate-100 hover:tw-text-slate-700 focus-visible:tw-outline-none focus-visible:tw-ring-4 focus-visible:tw-ring-slate-200"
          >
            <FiX size={18} />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

export default function CreateParking() {
  const db = useMemo(() => getFirestore(), []);
  const auth = useMemo(() => getAuth(), []);
  const navigate = useNavigate();

  const permissionItems = useMemo(() => flattenMenuItems(fullMenu), []);
  const allPermissionUrls = useMemo(
    () => permissionItems.map((item) => item.url),
    [permissionItems]
  );

  // AuthProvider бол login/session/role-ийн цорын ганц эх сурвалж.
  // Firebase listener-ийг энэ хуудсанд дахин бүртгэхгүй: анхны null
  // callback-оос болж login руу үсэрдэг race condition арилна.
  const {
    user: currentUser,
    role: authenticatedRole,
    loading: authChecking,
    isActive,
    profileMissing,
    error: authError,
  } = useAuth();
  // users/{uid} дахь role (эсвэл AuthProvider-ийн custom claim) ашиглана.
  // displayName болон parkings/{id}/users/{email}-д admin эрх шалгахгүй.
  // Fail closed: profile уншаагүй, disabled эсвэл error үед эрх олгохгүй.
  const myRole =
    !authChecking &&
    currentUser &&
    isActive === true &&
    !profileMissing &&
    !authError &&
    normalizeRole(authenticatedRole) === "superadmin"
      ? "superadmin"
      : "";

  const [activeTab, setActiveTab] = useState("parkings");
  const [parkings, setParkings] = useState([]);
  const [parkingSearch, setParkingSearch] = useState("");
  const [selectedParkingId, setSelectedParkingId] = useState("");
  const [parkingData, setParkingData] = useState(createDefaultData);
  const [loadingParkings, setLoadingParkings] = useState(false);
  const [loadingParking, setLoadingParking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState("");
  const [userPermissions, setUserPermissions] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(createNewParkingForm);
  const [creating, setCreating] = useState(false);
  const [gateLoading, setGateLoading] = useState("");

  // Бүртгэлтэй хэрэглэгчдийн хугацааны удирдлага
  const [registeredCars, setRegisteredCars] = useState([]);
  const [loadingRegisteredCars, setLoadingRegisteredCars] = useState(false);
  const [registeredCarSearch, setRegisteredCarSearch] = useState("");
  const [registeredCarFilter, setRegisteredCarFilter] = useState("all");
  const [selectedRegisteredCarIds, setSelectedRegisteredCarIds] = useState([]);
  const [bulkExpiryDays, setBulkExpiryDays] = useState("30");
  const [bulkExpiryScope, setBulkExpiryScope] = useState("selected");
  const [adjustingExpiry, setAdjustingExpiry] = useState(false);
  const [processingRegisteredCarId, setProcessingRegisteredCarId] = useState("");
  const [rowExpiryDays, setRowExpiryDays] = useState({});
  const [bulkExpiryAmount, setBulkExpiryAmount] = useState("0");
  const [rowExpiryAmounts, setRowExpiryAmounts] = useState({});

  // App/Web-ээс хэрэглэгч өөрөө хийсэн сунгалтын тайлан (parkings/{parkId}/renewals)
  const [extensionReportDate, setExtensionReportDate] = useState(() =>
    toDateInputValue(new Date())
  );
  const [extensionLogs, setExtensionLogs] = useState([]);
  const [loadingExtensionReport, setLoadingExtensionReport] = useState(false);
  const [renewalStatusFilter, setRenewalStatusFilter] = useState("success");


  const loadParkings = useCallback(async () => {
    if (authChecking || myRole !== "superadmin") return;

    setLoadingParkings(true);

    try {
      const snapshot = await getDocs(collection(db, "parkings"));

      const list = snapshot.docs
        .map((parkingDoc) => ({
          id: parkingDoc.id,
          ...parkingDoc.data(),
        }))
        .sort((a, b) =>
          String(a.name || a.id).localeCompare(
            String(b.name || b.id),
            "mn"
          )
        );

      setParkings(list);
    } catch (error) {
      console.error("Зогсоол татах алдаа:", error);
      Toast.fire({
        icon: "error",
        title:
          error?.code === "permission-denied"
            ? "Зогсоолын жагсаалт унших эрх хүрэлцэхгүй байна."
            : "Зогсоолын жагсаалт татагдсангүй.",
      });
    } finally {
      setLoadingParkings(false);
    }
  }, [authChecking, db, myRole]);

  useEffect(() => {
    loadParkings();
  }, [loadParkings]);

  const loadParking = useCallback(
    async (parkingId) => {
      if (!parkingId) {
        setParkingData(createDefaultData());
        setUsers([]);
        setSelectedUserEmail("");
        setUserPermissions([]);
        setDirty(false);
        return;
      }

      setLoadingParking(true);
      setLoadingUsers(true);

      try {
        const [parkingSnap, usersSnap] = await Promise.all([
          getDoc(doc(db, "parkings", parkingId)),
          getDocs(collection(db, "parkings", parkingId, "users")),
        ]);

        if (!parkingSnap.exists()) {
          throw new Error("Сонгосон зогсоол олдсонгүй.");
        }

        setParkingData(normalizeParkingData(parkingSnap.data()));
        setUsers(
          usersSnap.docs
            .map((userDoc) => ({
              email: userDoc.id,
              ...userDoc.data(),
            }))
            .sort((a, b) => String(a.email).localeCompare(String(b.email)))
        );
        setSelectedUserEmail("");
        setUserPermissions([]);
        setDirty(false);
      } catch (error) {
        console.error("Зогсоолын мэдээлэл татах алдаа:", error);
        Toast.fire({
          icon: "error",
          title: error?.message || "Мэдээлэл татагдсангүй.",
        });
      } finally {
        setLoadingParking(false);
        setLoadingUsers(false);
      }
    },
    [db]
  );

  useEffect(() => {
    loadParking(selectedParkingId);
  }, [loadParking, selectedParkingId]);

  useEffect(() => {
    const warnBeforeClose = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warnBeforeClose);
    return () => window.removeEventListener("beforeunload", warnBeforeClose);
  }, [dirty]);

  const filteredParkings = useMemo(() => {
    const query = parkingSearch.trim().toLowerCase();
    if (!query) return parkings;

    return parkings.filter((parking) => {
      const values = [parking.id, parking.name, parking.config?.address]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return values.includes(query);
    });
  }, [parkingSearch, parkings]);

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) =>
      String(user.email || "")
        .toLowerCase()
        .includes(query)
    );
  }, [userSearch, users]);

  const selectedUser = useMemo(
    () => users.find((user) => user.email === selectedUserEmail) || null,
    [selectedUserEmail, users]
  );

  const gateCameraRegistry = useMemo(
    () => normalizeGateCameraRegistry(parkingData.gate?.cameras),
    [parkingData.gate?.cameras]
  );

  const currentCameraPlan = useMemo(
    () =>
      normalizeCameraPlan(
        parkingData.gate?.cameraPlan,
        parkingData.gate?.cameras
      ),
    [
      parkingData.gate?.cameraPlan,
      parkingData.gate?.cameras,
    ]
  );

  const plannedGateCameraRows = useMemo(
    () =>
      buildPlannedCameraRows(
        currentCameraPlan,
        parkingData.gate?.cameras
      ),
    [
      currentCameraPlan,
      parkingData.gate?.cameras,
    ]
  );

  const actualConfiguredCounts = useMemo(
    () => ({
      enter: gateCameraRegistry.filter(
        (camera) =>
          camera.role === "enter" &&
          camera.configured !== false
      ).length,
      exit: gateCameraRegistry.filter(
        (camera) =>
          camera.role === "exit" &&
          camera.configured !== false
      ).length,
    }),
    [gateCameraRegistry]
  );

  const dashboardButtonRows = useMemo(() => {
    const buttonSettings = normalizeDashboardButtons(
      parkingData.gate?.dashboardButtons || {}
    );

    return plannedGateCameraRows
      .map((camera, index) => {
        const saved = buttonSettings[camera.camera_id] || {};
        const parsedOrder = Number(saved.order);

        return {
          ...camera,
          dashboardLabel:
            String(saved.label || "").trim() || camera.name || camera.camera_id,
          dashboardVisible: saved.visible !== false,
          dashboardOrder:
            Number.isFinite(parsedOrder) && parsedOrder >= 0
              ? Math.trunc(parsedOrder)
              : index + 1,
        };
      })
      .sort((a, b) => {
        const orderDiff = a.dashboardOrder - b.dashboardOrder;
        if (orderDiff !== 0) return orderDiff;

        if (a.role !== b.role) {
          return a.role === "enter" ? -1 : 1;
        }

        return a.camera_id.localeCompare(b.camera_id);
      });
  }, [plannedGateCameraRows, parkingData.gate?.dashboardButtons]);

  const updateCameraPlanCount = useCallback(
    (role, rawValue) => {
      const key =
        role === "exit"
          ? "exitCount"
          : "enterCount";

      const parsed =
        rawValue === ""
          ? ""
          : Number(rawValue);

      setParkingData((previous) => {
        const previousPlan =
          normalizeCameraPlan(
            previous.gate?.cameraPlan,
            previous.gate?.cameras
          );

        const nextPlan = {
          ...previousPlan,
          [key]: parsed,
        };

        const normalizedEnter =
          Number.isInteger(Number(nextPlan.enterCount))
            ? Math.min(
                32,
                Math.max(
                  0,
                  Number(nextPlan.enterCount)
                )
              )
            : 0;

        const normalizedExit =
          Number.isInteger(Number(nextPlan.exitCount))
            ? Math.min(
                32,
                Math.max(
                  0,
                  Number(nextPlan.exitCount)
                )
              )
            : 0;

        const safePlan = {
          enterCount:
            rawValue === "" &&
            key === "enterCount"
              ? ""
              : normalizedEnter,
          exitCount:
            rawValue === "" &&
            key === "exitCount"
              ? ""
              : normalizedExit,
          totalCount:
            normalizedEnter + normalizedExit,
        };

        return {
          ...previous,
          gate: {
            ...previous.gate,
            cameraPlan: safePlan,
            dashboardButtons:
              reconcileDashboardButtons(
                previous.gate?.dashboardButtons,
                {
                  enterCount: normalizedEnter,
                  exitCount: normalizedExit,
                  totalCount:
                    normalizedEnter +
                    normalizedExit,
                },
                previous.gate?.cameras
              ),
          },
        };
      });

      setDirty(true);
    },
    []
  );

  const updateDashboardButton = useCallback(
    (cameraId, field, value) => {
      const normalizedId = String(cameraId || "")
        .trim()
        .toLowerCase();

      if (!normalizedId) return;

      setParkingData((previous) => {
        const currentButtons = normalizeDashboardButtons(
          previous.gate?.dashboardButtons || {}
        );

        const current = currentButtons[normalizedId] || {
          cameraId: normalizedId,
          label: "",
          visible: true,
          order: 0,
        };

        let nextValue = value;

        if (field === "order") {
          const parsed = Number(value);
          nextValue =
            Number.isFinite(parsed) && parsed >= 0
              ? Math.trunc(parsed)
              : 0;
        } else if (field === "visible") {
          nextValue = Boolean(value);
        } else if (field === "label") {
          nextValue = String(value || "").slice(0, 80);
        }

        return {
          ...previous,
          gate: {
            ...previous.gate,
            dashboardButtons: {
              ...currentButtons,
              [normalizedId]: {
                ...current,
                cameraId: normalizedId,
                [field]: nextValue,
              },
            },
          },
        };
      });

      setDirty(true);
    },
    []
  );

  const resetDashboardButton = useCallback((camera) => {
    const cameraId = String(camera?.camera_id || "")
      .trim()
      .toLowerCase();

    if (!cameraId) return;

    setParkingData((previous) => {
      const currentButtons = normalizeDashboardButtons(
        previous.gate?.dashboardButtons || {}
      );

      const nextButtons = { ...currentButtons };
      delete nextButtons[cameraId];

      return {
        ...previous,
        gate: {
          ...previous.gate,
          dashboardButtons: nextButtons,
        },
      };
    });

    setDirty(true);
  }, []);

  const handleSectionChange = (section, event) => {
    const { name, value, type, checked } = event.target;

    let finalValue = value;
    if (type === "checkbox") {
      finalValue = checked;
    } else if (NUMBER_FIELDS.has(name)) {
      finalValue = value === "" ? "" : Number(value);
    }

    setParkingData((previous) => ({
      ...previous,
      [section]: {
        ...previous[section],
        [name]: finalValue,
      },
    }));
    setDirty(true);
  };

  const setBooleanValue = (section, name, value) => {
    setParkingData((previous) => ({
      ...previous,
      [section]: {
        ...previous[section],
        [name]: value,
      },
    }));
    setDirty(true);
  };

  const saveParking = async () => {
    if (!selectedParkingId || saving) return;

    const validationErrors = validateParkingConfig(parkingData);
    if (validationErrors.length > 0) {
      await MySwal.fire({
        icon: "warning",
        title: "Тохиргоогоо шалгана уу",
        text: validationErrors[0],
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const parkingName = String(
      parkingData.root.name || parkingData.config.name || ""
    ).trim();
    const rootJoinCode = String(parkingData.root.joinCode || "").trim();
    const gateJoinCode = String(parkingData.gate.joinCode || "").trim();

    const enterCameraCount = Number(
      parkingData.gate?.cameraPlan?.enterCount
    );
    const exitCameraCount = Number(
      parkingData.gate?.cameraPlan?.exitCount
    );

    if (
      !Number.isInteger(enterCameraCount) ||
      enterCameraCount < 0 ||
      enterCameraCount > 32
    ) {
      await MySwal.fire({
        icon: "warning",
        title: "Орох камерын тоог шалгана уу",
        text: "0–32 хооронд бүхэл тоо оруулна уу.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    if (
      !Number.isInteger(exitCameraCount) ||
      exitCameraCount < 0 ||
      exitCameraCount > 32
    ) {
      await MySwal.fire({
        icon: "warning",
        title: "Гарах камерын тоог шалгана уу",
        text: "0–32 хооронд бүхэл тоо оруулна уу.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    if (
      enterCameraCount + exitCameraCount < 1 ||
      enterCameraCount + exitCameraCount > 64
    ) {
      await MySwal.fire({
        icon: "warning",
        title: "Нийт камерын тоог шалгана уу",
        text: "Нийт камер 1–64 хооронд байна.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const normalizedCameraPlan = {
      enterCount: enterCameraCount,
      exitCount: exitCameraCount,
      totalCount:
        enterCameraCount + exitCameraCount,
    };

    const normalizedDashboardButtons =
      reconcileDashboardButtons(
        parkingData.gate?.dashboardButtons || {},
        normalizedCameraPlan,
        parkingData.gate?.cameras
      );

    setSaving(true);

    try {
      const normalizedConfig = {
        ...parkingData.config,
        name: parkingName,
        CONFIG_PRICE_LARGE: String(
          parkingData.config.CONFIG_PRICE_LARGE
        ),
        FREE_MINUTES: String(parkingData.config.FREE_MINUTES),
        INVITED_FREE_MINUTES: Number(
          parkingData.config.INVITED_FREE_MINUTES
        ),
        FIXED_PRICE: String(parkingData.config.FIXED_PRICE),
        FIXED_PRICE_LARGE: String(
          parkingData.config.FIXED_PRICE_LARGE
        ),
        ebarimt: normalizeBoolean(
          parkingData.config.ebarimt,
          false
        ),
      };

      // Gate-ийн enterOpen / exitOpen командын төлөвийг эндээс дарж бичихгүй.
      await updateDoc(doc(db, "parkings", selectedParkingId), {
        name: parkingName,
        joinCode: rootJoinCode,
        config: normalizedConfig,
        ebarimt: { terminalNo: String(parkingData.ebarimt.terminalNo || "") },
        "gate.joinCode": gateJoinCode,
        "gate.cameraPlan": normalizedCameraPlan,
        // Hardware registry (gate.cameras), live commands, enterOpen/exitOpen
        // are intentionally not overwritten by this settings save.
        "gate.dashboardButtons": normalizedDashboardButtons,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || "",
      });

      setParkingData((previous) => ({
        ...previous,
        root: { ...previous.root, name: parkingName, joinCode: rootJoinCode },
        config: { ...previous.config, ...normalizedConfig },
        gate: {
          ...previous.gate,
          joinCode: gateJoinCode,
          cameraPlan: normalizedCameraPlan,
          dashboardButtons: normalizedDashboardButtons,
        },
      }));

      setParkings((previous) =>
        previous.map((parking) =>
          parking.id === selectedParkingId
            ? {
                ...parking,
                name: parkingName,
                joinCode: rootJoinCode,
                config: normalizedConfig,
              }
            : parking
        )
      );

      setDirty(false);
      Toast.fire({ icon: "success", title: "Тохиргоо хадгалагдлаа." });
    } catch (error) {
      console.error("Тохиргоо хадгалах алдаа:", error);
      Toast.fire({
        icon: "error",
        title:
          error?.code === "permission-denied"
            ? "Хадгалах эрх хүрэлцэхгүй байна."
            : "Хадгалах үед алдаа гарлаа.",
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDiscardChanges = useCallback(async () => {
    if (!dirty) return true;

    const result = await MySwal.fire({
      icon: "warning",
      title: "Хадгалаагүй өөрчлөлт байна",
      text: "Үргэлжлүүлбэл хадгалаагүй өөрчлөлт устах болно.",
      showCancelButton: true,
      confirmButtonText: "Үргэлжлүүлэх",
      cancelButtonText: "Буцах",
      confirmButtonColor: "#2563eb",
      reverseButtons: true,
    });

    return result.isConfirmed;
  }, [dirty]);

  const selectParking = async (parkingId) => {
    if (parkingId !== selectedParkingId && !(await confirmDiscardChanges())) {
      return;
    }

    setSelectedParkingId(parkingId);
    setExtensionLogs([]);
    setActiveTab("settings");
  };

  const handleCreateFormChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((previous) => ({ ...previous, [name]: value }));
  };

  const closeCreateModal = useCallback(() => {
    if (creating) return;
    setCreateOpen(false);
    setCreateForm(createNewParkingForm());
  }, [creating]);

  const createParking = async (event) => {
    event.preventDefault();

    if (myRole !== "superadmin" || creating) {
      Toast.fire({
        icon: "error",
        title: "Шинэ зогсоол үүсгэх эрх зөвхөн Super Admin-д байна.",
      });
      return;
    }

    const parkingId = createForm.parkingId.trim();
    const name = createForm.name.trim();
    const joinCode = createForm.joinCode.trim();
    const address = createForm.address.trim();
    const managerEmail = normalizeEmail(createForm.managerEmail);
    const freeMinutes = Number(createForm.freeMinutes);
    const invitedFreeMinutes = Number(createForm.invitedFreeMinutes);
    const hourlyPrice = Number(createForm.hourlyPrice);
    const largeVehiclePrice = Number(createForm.largeVehiclePrice);
    const fixedLargeVehiclePrice = Number(
      createForm.fixedLargeVehiclePrice
    );
    const enterCameraCount = Number(
      createForm.enterCameraCount
    );
    const exitCameraCount = Number(
      createForm.exitCameraCount
    );

    if (!PARKING_ID_PATTERN.test(parkingId)) {
      Toast.fire({
        icon: "warning",
        title: "ID нь 3–64 тэмдэгттэй, зөвхөн латин үсэг, тоо, _ эсвэл - байна.",
      });
      return;
    }

    if (!name || !joinCode) {
      Toast.fire({ icon: "warning", title: "Нэр болон Join Code оруулна уу." });
      return;
    }

    if (!Number.isFinite(freeMinutes) || freeMinutes < 0) {
      Toast.fire({ icon: "warning", title: "Үнэгүй хугацааг зөв оруулна уу." });
      return;
    }

    if (
      !Number.isFinite(invitedFreeMinutes) ||
      invitedFreeMinutes < 0 ||
      invitedFreeMinutes > 7 * 24 * 60
    ) {
      Toast.fire({
        icon: "warning",
        title: "Урилгатай зочны үнэгүй хугацаа 0–10080 минут байна.",
      });
      return;
    }

    if (!Number.isFinite(hourlyPrice) || hourlyPrice < 0) {
      Toast.fire({ icon: "warning", title: "Цагийн үнийг зөв оруулна уу." });
      return;
    }

    if (!Number.isFinite(largeVehiclePrice) || largeVehiclePrice < 0) {
      Toast.fire({
        icon: "warning",
        title: "Том машины цагийн үнийг зөв оруулна уу.",
      });
      return;
    }

    if (
      !Number.isFinite(fixedLargeVehiclePrice) ||
      fixedLargeVehiclePrice < 0
    ) {
      Toast.fire({
        icon: "warning",
        title: "Том машины тогтмол үнийг зөв оруулна уу.",
      });
      return;
    }

    if (
      !Number.isInteger(enterCameraCount) ||
      enterCameraCount < 0 ||
      enterCameraCount > 32
    ) {
      Toast.fire({
        icon: "warning",
        title: "Орох камерын тоо 0–32 хооронд бүхэл тоо байна.",
      });
      return;
    }

    if (
      !Number.isInteger(exitCameraCount) ||
      exitCameraCount < 0 ||
      exitCameraCount > 32
    ) {
      Toast.fire({
        icon: "warning",
        title: "Гарах камерын тоо 0–32 хооронд бүхэл тоо байна.",
      });
      return;
    }

    if (
      enterCameraCount + exitCameraCount < 1 ||
      enterCameraCount + exitCameraCount > 64
    ) {
      Toast.fire({
        icon: "warning",
        title: "Нийт камерын тоо 1–64 хооронд байна.",
      });
      return;
    }

    if (managerEmail && !EMAIL_PATTERN.test(managerEmail)) {
      Toast.fire({ icon: "warning", title: "Master хэрэглэгчийн имэйл буруу байна." });
      return;
    }

    setCreating(true);

    try {
      const parkingRef = doc(db, "parkings", parkingId);
      const existing = await getDoc(parkingRef);

      if (existing.exists()) {
        Toast.fire({ icon: "warning", title: "Ийм ID-тай зогсоол байна." });
        return;
      }

      const defaults = createDefaultData();
      const config = {
        ...defaults.config,
        name,
        joinCode,
        address,
        CONFIG_PRICE_LARGE: String(largeVehiclePrice),
        FIXED_PRICE_LARGE: String(fixedLargeVehiclePrice),
        FREE_MINUTES: String(freeMinutes),
        INVITED_FREE_MINUTES: invitedFreeMinutes,
        PRICE_PER_HOUR: hourlyPrice,
        HOURLY_RATE: hourlyPrice,
        priceParking: hourlyPrice,
      };

      const initialCameraRegistry =
        buildInitialGateCameraRegistry(
          enterCameraCount,
          exitCameraCount
        );

      const initialDashboardButtons =
        buildInitialDashboardButtons(
          initialCameraRegistry
        );

      const batch = writeBatch(db);
      batch.set(parkingRef, {
        name,
        joinCode,
        config,
        ebarimt: defaults.ebarimt,
        gate: {
          ...defaults.gate,
          joinCode,
          cameraPlan: {
            enterCount: enterCameraCount,
            exitCount: exitCameraCount,
            totalCount:
              enterCameraCount + exitCameraCount,
          },
          cameras: initialCameraRegistry,
          dashboardButtons:
            initialDashboardButtons,
        },
        createdAt: serverTimestamp(),
        createdBy: currentUser?.email || "",
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || "",
      });

      if (managerEmail) {
        batch.set(
          doc(db, "parkings", parkingId, "users", managerEmail),
          {
            email: managerEmail,
            role: "admin",
            isMaster: true,
            permissions: allPermissionUrls,
            createdAt: serverTimestamp(),
            createdBy: currentUser?.email || "",
            updatedAt: serverTimestamp(),
            updatedBy: currentUser?.email || "",
          },
          { merge: true }
        );
      }

      await batch.commit();
      await loadParkings();

      setCreateOpen(false);
      setCreateForm(createNewParkingForm());
      setSelectedParkingId(parkingId);
      setActiveTab("settings");

      await MySwal.fire({
        icon: "success",
        title: "Зогсоол амжилттай үүслээ",
        text: managerEmail
          ? `${name} — ${enterCameraCount} орох, ${exitCameraCount} гарах камерын төлөвлөгөөтэй үүслээ. ${managerEmail} хэрэглэгч Master эрхтэй бүртгэгдлээ.`
          : `${name} — ${enterCameraCount} орох, ${exitCameraCount} гарах камерын төлөвлөгөөтэй үүслээ.`,
        confirmButtonColor: "#2563eb",
      });
    } catch (error) {
      console.error("Зогсоол үүсгэх алдаа:", error);
      Toast.fire({
        icon: "error",
        title:
          error?.code === "permission-denied"
            ? "Зогсоол үүсгэх эрх хүрэлцэхгүй байна."
            : "Зогсоол үүсгэхэд алдаа гарлаа.",
      });
    } finally {
      setCreating(false);
    }
  };


  const loadRegisteredCars = useCallback(async () => {
    if (!selectedParkingId || myRole !== "superadmin") {
      setRegisteredCars([]);
      setSelectedRegisteredCarIds([]);
      return;
    }

    setLoadingRegisteredCars(true);

    try {
      const snapshot = await getDocs(
        collection(db, "parkings", selectedParkingId, "registeredCars")
      );

      const items = snapshot.docs.map((carDoc) => ({
        id: carDoc.id,
        ...carDoc.data(),
      }));

      items.sort((a, b) => {
        const plateA = String(a.plate || a.id || "");
        const plateB = String(b.plate || b.id || "");
        return plateA.localeCompare(plateB, "mn");
      });

      setRegisteredCars(items);
      setSelectedRegisteredCarIds((previous) =>
        previous.filter((id) => items.some((item) => item.id === id))
      );
    } catch (error) {
      console.error("registeredCars татах алдаа:", error);
      Toast.fire({
        icon: "error",
        title:
          error?.code === "permission-denied"
            ? "Бүртгэлтэй хэрэглэгчдийг унших эрх хүрэлцэхгүй байна."
            : "Бүртгэлтэй хэрэглэгчдийн мэдээлэл татагдсангүй.",
      });
    } finally {
      setLoadingRegisteredCars(false);
    }
  }, [db, myRole, selectedParkingId]);

  useEffect(() => {
    if (activeTab !== "registeredCars" || !selectedParkingId) return;
    loadRegisteredCars();
  }, [activeTab, loadRegisteredCars, selectedParkingId]);

  const getRegisteredCarExpireDate = useCallback((car) => {
    const raw = car?.expireDate;
    if (!raw) return null;

    const value =
      typeof raw?.toDate === "function" ? raw.toDate() : new Date(raw);

    return value instanceof Date && !Number.isNaN(value.getTime())
      ? value
      : null;
  }, []);

  const getRegisteredCarStatus = useCallback(
    (car) => {
      const expireDate = getRegisteredCarExpireDate(car);
      if (!expireDate) return "expired";
      return expireDate.getTime() > Date.now() ? "active" : "expired";
    },
    [getRegisteredCarExpireDate]
  );

  const registeredCarStats = useMemo(() => {
    let active = 0;
    let expired = 0;

    registeredCars.forEach((car) => {
      if (getRegisteredCarStatus(car) === "active") active += 1;
      else expired += 1;
    });

    return {
      total: registeredCars.length,
      active,
      expired,
    };
  }, [getRegisteredCarStatus, registeredCars]);

  const filteredRegisteredCars = useMemo(() => {
    const search = registeredCarSearch.trim().toLowerCase();

    return registeredCars.filter((car) => {
      const status = getRegisteredCarStatus(car);
      if (registeredCarFilter !== "all" && status !== registeredCarFilter) {
        return false;
      }

      if (!search) return true;

      const haystack = [
        car.plate,
        car.userName,
        car.name,
        car.phone,
        car.address,
        car.building,
        car.apartment,
        car.number,
        car.uid,
        car.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(search);
    });
  }, [
    getRegisteredCarStatus,
    registeredCarFilter,
    registeredCarSearch,
    registeredCars,
  ]);

  const filteredRegisteredCarIds = useMemo(
    () => filteredRegisteredCars.map((car) => car.id),
    [filteredRegisteredCars]
  );

  const allFilteredRegisteredCarsSelected =
    filteredRegisteredCarIds.length > 0 &&
    filteredRegisteredCarIds.every((id) => selectedRegisteredCarIds.includes(id));

  const toggleRegisteredCarSelection = (id) => {
    setSelectedRegisteredCarIds((previous) =>
      previous.includes(id)
        ? previous.filter((item) => item !== id)
        : [...previous, id]
    );
  };

  const toggleAllFilteredRegisteredCars = () => {
    setSelectedRegisteredCarIds((previous) => {
      if (allFilteredRegisteredCarsSelected) {
        return previous.filter((id) => !filteredRegisteredCarIds.includes(id));
      }

      return Array.from(new Set([...previous, ...filteredRegisteredCarIds]));
    });
  };

  const loadExtensionReport = useCallback(async () => {
    if (!selectedParkingId || myRole !== "superadmin" || !extensionReportDate) {
      setExtensionLogs([]);
      return;
    }

    setLoadingExtensionReport(true);

    try {
      const dayStart = new Date(`${extensionReportDate}T00:00:00`);
      const nextDay = new Date(dayStart);
      nextDay.setDate(nextDay.getDate() + 1);

      const renewalsRef = collection(
        db,
        "parkings",
        selectedParkingId,
        "renewals"
      );

      // Зөвхөн сонгосон өдрийн renewal document-уудыг уншина.
      // status-ыг client талд шүүх тул нэмэлт composite index шаардахгүй.
      const renewalsQuery = query(
        renewalsRef,
        where("createdAt", ">=", dayStart),
        where("createdAt", "<", nextDay)
      );

      const snapshot = await getDocs(renewalsQuery);
      const items = snapshot.docs.map((renewalDoc) => ({
        id: renewalDoc.id,
        ...renewalDoc.data(),
      }));

      items.sort((a, b) => {
        const aDate = toSafeDate(a.createdAt);
        const bDate = toSafeDate(b.createdAt);
        return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
      });

      setExtensionLogs(items);
    } catch (error) {
      console.error("Renewals тайлан татах алдаа:", error);
      setExtensionLogs([]);
      Toast.fire({
        icon: "error",
        title:
          error?.code === "permission-denied"
            ? "renewals мэдээлэл унших эрх хүрэлцэхгүй байна."
            : "Сунгалтын тайлан татагдсангүй.",
      });
    } finally {
      setLoadingExtensionReport(false);
    }
  }, [db, extensionReportDate, myRole, selectedParkingId]);

  useEffect(() => {
    if (activeTab !== "extensionReport" || !selectedParkingId) return;
    loadExtensionReport();
  }, [activeTab, loadExtensionReport, selectedParkingId]);

  const successfulRenewalLogs = useMemo(
    () =>
      extensionLogs.filter(
        (item) => String(item.status || "").trim().toLowerCase() === "success"
      ),
    [extensionLogs]
  );

  const visibleRenewalLogs = useMemo(() => {
    if (renewalStatusFilter === "all") return extensionLogs;

    return extensionLogs.filter(
      (item) =>
        String(item.status || "").trim().toLowerCase() === renewalStatusFilter
    );
  }, [extensionLogs, renewalStatusFilter]);

  const extensionReportStats = useMemo(() => {
    const uniqueUsers = new Set(
      successfulRenewalLogs
        .map((item) =>
          String(item.userId || item.plate || "")
            .trim()
            .toUpperCase()
        )
        .filter(Boolean)
    );

    return {
      people: uniqueUsers.size,
      transactions: successfulRenewalLogs.length,
      totalMonths: successfulRenewalLogs.reduce(
        (sum, item) => sum + Math.max(0, Number(item.monthsExtended) || 0),
        0
      ),
      totalAmount: successfulRenewalLogs.reduce(
        (sum, item) => sum + Math.max(0, Number(item.amount) || 0),
        0
      ),
      otherTransactions: Math.max(
        0,
        extensionLogs.length - successfulRenewalLogs.length
      ),
      allTransactions: extensionLogs.length,
    };
  }, [extensionLogs, successfulRenewalLogs]);

  const calculateAdjustedExpireDate = useCallback(
    (car, signedDays) => {
      const currentExpireDate = getRegisteredCarExpireDate(car);
      const now = new Date();

      if (signedDays < 0 && !currentExpireDate) {
        return null;
      }

      const baseDate =
        signedDays > 0
          ? currentExpireDate && currentExpireDate > now
            ? currentExpireDate
            : now
          : currentExpireDate;

      if (!baseDate) return null;

      const result = new Date(baseDate);
      result.setDate(result.getDate() + signedDays);
      return result;
    },
    [getRegisteredCarExpireDate]
  );

  const adjustRegisteredCarsExpiry = useCallback(
    async ({ carIds, signedDays, source = "bulk", amountPerUser = 0 }) => {
      if (
        myRole !== "superadmin" ||
        !selectedParkingId ||
        adjustingExpiry ||
        !Array.isArray(carIds) ||
        carIds.length === 0
      ) {
        return;
      }

      const days = Math.abs(Number(signedDays));
      if (!Number.isInteger(days) || days < 1 || days > 3650) {
        await MySwal.fire({
          icon: "warning",
          title: "Хоногийн утгаа шалгана уу",
          text: "1–3650 хүртэл бүхэл хоног оруулна уу.",
          confirmButtonColor: "#2563eb",
        });
        return;
      }

      const normalizedSignedDays = signedDays < 0 ? -days : days;
      const isSubtract = normalizedSignedDays < 0;
      const normalizedAmount = isSubtract ? 0 : Number(amountPerUser);

      if (
        !isSubtract &&
        (!Number.isFinite(normalizedAmount) || normalizedAmount < 0)
      ) {
        await MySwal.fire({
          icon: "warning",
          title: "Төлбөрийн дүнгээ шалгана уу",
          text: "Төлбөр 0 эсвэл түүнээс их тоо байна.",
          confirmButtonColor: "#2563eb",
        });
        return;
      }

      const amountEach = isSubtract ? 0 : Math.round(normalizedAmount);
      const actionText = isSubtract ? "хасах" : "нэмэх";
      const parkingInfo = parkings.find(
        (parking) => parking.id === selectedParkingId
      );
      const parkingLabel =
        parkingInfo?.name || parkingInfo?.config?.name || selectedParkingId;

      const confirmResult = await MySwal.fire({
        icon: isSubtract ? "warning" : "question",
        title: `${days} хоног ${actionText} уу?`,
        html:
          `<div style="text-align:left;line-height:1.65">` +
          `<b>${parkingLabel}</b> зогсоолын <b>${carIds.length}</b> хэрэглэгчид үйлчилнэ.<br/>` +
          (isSubtract
            ? `Одоогийн expireDate-оос ${days} хоног хасна. Хугацаа байхгүй хэрэглэгч алгасагдана.`
            : `Хугацаа идэвхтэй бол одоогийн expireDate-оос, дууссан бол өнөөдрөөс ${days} хоног нэмнэ.<br/>` +
              `1 хэрэглэгчийн төлбөр: <b>${formatCurrency(amountEach)}</b>`) +
          `</div>`,
        showCancelButton: true,
        confirmButtonText: isSubtract ? "Хугацаа хасах" : "Хугацаа нэмэх",
        cancelButtonText: "Болих",
        confirmButtonColor: isSubtract ? "#dc2626" : "#2563eb",
        reverseButtons: true,
      });

      if (!confirmResult.isConfirmed) return;

      setAdjustingExpiry(true);
      if (source !== "bulk" && carIds.length === 1) {
        setProcessingRegisteredCarId(carIds[0]);
      }

      try {
        const selectedIdSet = new Set(carIds);
        const targetCars = registeredCars.filter((car) => selectedIdSet.has(car.id));
        const batchLimit = 450;
        let batch = writeBatch(db);
        let pendingWrites = 0;
        let updatedCount = 0;
        let skippedCount = 0;
        let totalCollected = 0;
        const nextExpireDates = new Map();

        const commitCurrentBatch = async () => {
          if (pendingWrites === 0) return;
          await batch.commit();
          batch = writeBatch(db);
          pendingWrites = 0;
        };

        for (const car of targetCars) {
          const newExpireDate = calculateAdjustedExpireDate(
            car,
            normalizedSignedDays
          );

          if (!newExpireDate) {
            skippedCount += 1;
            continue;
          }

          const previousExpireDate = getRegisteredCarExpireDate(car);
          const carRef = doc(
            db,
            "parkings",
            selectedParkingId,
            "registeredCars",
            car.id
          );
          const historyRef = doc(
            collection(
              db,
              "parkings",
              selectedParkingId,
              "membershipExtensions"
            )
          );

          batch.update(carRef, {
            expireDate: newExpireDate,
            updatedAt: serverTimestamp(),
            expiryAdjustedAt: serverTimestamp(),
            expiryAdjustedBy: currentUser?.email || "",
            expiryAdjustmentDays: normalizedSignedDays,
            lastExtensionAmount: amountEach,
          });

          batch.set(historyRef, {
            parkingId: selectedParkingId,
            parkingName: parkingLabel,
            carId: car.id,
            plate: car.plate || car.id || "",
            userName: car.userName || car.name || "",
            phone: car.phone || "",
            building: car.building || car.address || "",
            number: car.number || car.apartment || "",
            daysDelta: normalizedSignedDays,
            amount: amountEach,
            previousExpireDate: previousExpireDate || null,
            newExpireDate,
            actionType: isSubtract ? "reduce" : "extend",
            source,
            createdAt: serverTimestamp(),
            createdBy: currentUser?.email || "",
          });

          nextExpireDates.set(car.id, newExpireDate);
          pendingWrites += 2;
          updatedCount += 1;
          totalCollected += amountEach;

          if (pendingWrites >= batchLimit - 2) {
            await commitCurrentBatch();
          }
        }

        await commitCurrentBatch();

        if (updatedCount > 0) {
          await updateDoc(doc(db, "parkings", selectedParkingId), {
            lastRegisteredCarsExpiryAdjustment: {
              days: normalizedSignedDays,
              userCount: updatedCount,
              skippedCount,
              amountPerUser: amountEach,
              totalAmount: totalCollected,
              updatedAt: serverTimestamp(),
              updatedBy: currentUser?.email || "",
            },
          });
        }

        setRegisteredCars((previous) =>
          previous.map((car) =>
            nextExpireDates.has(car.id)
              ? { ...car, expireDate: nextExpireDates.get(car.id) }
              : car
          )
        );

        await MySwal.fire({
          icon: "success",
          title: "Хугацаа шинэчлэгдлээ",
          text:
            `${updatedCount} хэрэглэгчийн хугацаанд ${normalizedSignedDays > 0 ? "+" : ""}${normalizedSignedDays} хоногийн өөрчлөлт хийлээ.` +
            (!isSubtract ? ` Нийт төлбөр: ${formatCurrency(totalCollected)}.` : "") +
            (skippedCount > 0
              ? ` Хугацаагүй ${skippedCount} хэрэглэгч алгасагдсан.`
              : ""),
          confirmButtonColor: "#2563eb",
        });
      } catch (error) {
        console.error("registeredCars хугацаа өөрчлөх алдаа:", error);
        await MySwal.fire({
          icon: "error",
          title: "Хугацаа шинэчилж чадсангүй",
          text:
            error?.code === "permission-denied"
              ? "registeredCars шинэчлэх эрх хүрэлцэхгүй байна."
              : error?.message || "Дахин оролдоно уу.",
          confirmButtonColor: "#dc2626",
        });
      } finally {
        setAdjustingExpiry(false);
        setProcessingRegisteredCarId("");
      }
    },
    [
      adjustingExpiry,
      calculateAdjustedExpireDate,
      currentUser?.email,
      db,
      getRegisteredCarExpireDate,
      myRole,
      registeredCars,
      parkings,
      selectedParkingId,
    ]
  );

  const handleBulkExpiryAdjustment = async (direction) => {
    const days = Number(bulkExpiryDays);
    const carIds =
      bulkExpiryScope === "filtered"
        ? filteredRegisteredCarIds
        : selectedRegisteredCarIds;

    await adjustRegisteredCarsExpiry({
      carIds,
      signedDays: direction === "subtract" ? -days : days,
      source: "bulk",
      amountPerUser: direction === "subtract" ? 0 : Number(bulkExpiryAmount || 0),
    });
  };

  const handleSingleExpiryAdjustment = async (car, direction) => {
    const days = Number(rowExpiryDays[car.id] || 30);

    await adjustRegisteredCarsExpiry({
      carIds: [car.id],
      signedDays: direction === "subtract" ? -days : days,
      source: "single",
      amountPerUser:
        direction === "subtract" ? 0 : Number(rowExpiryAmounts[car.id] || 0),
    });
  };

  const openGate = async (cameraOrLegacy) => {
    if (!selectedParkingId || gateLoading) return;

    const isLegacy = typeof cameraOrLegacy === "string";
    const legacyGateName = isLegacy ? cameraOrLegacy : "";

    const cameraId = isLegacy
      ? legacyGateName === "enterOpen"
        ? "enter_1"
        : "exit_1"
      : String(cameraOrLegacy?.camera_id || "")
          .trim()
          .toLowerCase();

    const role = isLegacy
      ? legacyGateName === "enterOpen"
        ? "enter"
        : "exit"
      : cameraOrLegacy?.role === "exit"
        ? "exit"
        : "enter";

    const title = isLegacy
      ? role === "enter"
        ? "Орох хаалга"
        : "Гарах хаалга"
      : String(
          cameraOrLegacy?.name ||
            (role === "enter" ? "Орох хаалга" : "Гарах хаалга")
        ).trim();

    if (!cameraId) {
      Toast.fire({
        icon: "warning",
        title: "Camera ID олдсонгүй.",
      });
      return;
    }

    const result = await MySwal.fire({
      icon: "question",
      title: `${title} нээх үү?`,
      text: isLegacy
        ? "Legacy gate command илгээгдэнэ."
        : `${cameraId} төхөөрөмжид OPEN command илгээгдэнэ.`,
      showCancelButton: true,
      confirmButtonText: "Нээх",
      cancelButtonText: "Болих",
      confirmButtonColor: "#2563eb",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setGateLoading(cameraId);

    try {
      const parkingRef = doc(db, "parkings", selectedParkingId);

      if (isLegacy) {
        await updateDoc(parkingRef, {
          [`gate.${legacyGateName}`]: true,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || "",
        });
      } else {
        const requestId = createDashboardGateRequestId();

        await updateDoc(parkingRef, {
          [`gate.commands.${cameraId}`]: {
            requestId,
            cameraId,
            role,
            action: "OPEN",
            status: "PENDING",
            requestedAt: serverTimestamp(),
            requestedBy: currentUser?.email || currentUser?.uid || "superadmin",
            source: "CreateParking",
          },
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || "",
        });
      }

      Toast.fire({
        icon: "success",
        title: `${title} нээх команд илгээлээ.`,
      });
    } catch (error) {
      console.error("Gate командын алдаа:", error);
      Toast.fire({
        icon: "error",
        title:
          error?.code === "permission-denied"
            ? "Хаалга удирдах эрх хүрэлцэхгүй байна."
            : "Хаалганы команд илгээгдсэнгүй.",
      });
    } finally {
      setGateLoading("");
    }
  };

  const handleUserSelect = (email) => {
    setSelectedUserEmail(email);
    const user = users.find((item) => item.email === email);
    setUserPermissions(Array.isArray(user?.permissions) ? user.permissions : []);
  };

  const togglePermission = (url) => {
    setUserPermissions((previous) =>
      previous.includes(url)
        ? previous.filter((permission) => permission !== url)
        : [...previous, url]
    );
  };

  const toggleAllPermissions = () => {
    const hasAll = allPermissionUrls.every((url) => userPermissions.includes(url));
    setUserPermissions(hasAll ? [] : allPermissionUrls);
  };

  const savePermissions = async () => {
    if (!selectedParkingId || !selectedUserEmail || !selectedUser) return;

    setSavingPermissions(true);

    try {
      await updateDoc(
        doc(db, "parkings", selectedParkingId, "users", selectedUserEmail),
        {
          permissions: userPermissions,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || "",
        }
      );

      setUsers((previous) =>
        previous.map((user) =>
          user.email === selectedUserEmail
            ? { ...user, permissions: userPermissions }
            : user
        )
      );

      Toast.fire({ icon: "success", title: "Хэрэглэгчийн эрх хадгалагдлаа." });
    } catch (error) {
      console.error("Эрх хадгалах алдаа:", error);
      Toast.fire({ icon: "error", title: "Эрх хадгалагдсангүй." });
    } finally {
      setSavingPermissions(false);
    }
  };

  const toggleMaster = async () => {
    if (
      myRole !== "superadmin" ||
      !selectedParkingId ||
      !selectedUserEmail ||
      !selectedUser
    ) {
      return;
    }

    const nextValue = selectedUser.isMaster !== true;

    const result = await MySwal.fire({
      icon: "warning",
      title: nextValue ? "Master эрх олгох уу?" : "Master эрх цуцлах уу?",
      text: nextValue
        ? "Master хэрэглэгч тухайн зогсоолын бүх тохиргоонд хандана."
        : "Хэрэглэгч зөвхөн сонгосон цэснүүдэд хандана.",
      showCancelButton: true,
      confirmButtonText: nextValue ? "Олгох" : "Цуцлах",
      cancelButtonText: "Болих",
      confirmButtonColor: nextValue ? "#d97706" : "#475569",
    });

    if (!result.isConfirmed) return;

    setSavingPermissions(true);

    try {
      await updateDoc(
        doc(db, "parkings", selectedParkingId, "users", selectedUserEmail),
        {
          isMaster: nextValue,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || "",
        }
      );

      setUsers((previous) =>
        previous.map((user) =>
          user.email === selectedUserEmail
            ? { ...user, isMaster: nextValue }
            : user
        )
      );

      Toast.fire({
        icon: "success",
        title: nextValue ? "Master эрх олголоо." : "Master эрх цуцлагдлаа.",
      });
    } catch (error) {
      console.error("Master эрх шинэчлэх алдаа:", error);
      Toast.fire({ icon: "error", title: "Master эрх шинэчлэгдсэнгүй." });
    } finally {
      setSavingPermissions(false);
    }
  };

  const goToDashboard = async () => {
    if (!(await confirmDiscardChanges())) return;
    navigate("/dashboard");
  };

  const changeTab = (tabId) => {
    if (tabId === activeTab) return;
    // Дотоод tab солиход form state хадгалагдана.
    setActiveTab(tabId);
  };

  const logout = async () => {
    if (!(await confirmDiscardChanges())) return;

    try {
      await signOut(auth);
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Гарах үеийн алдаа:", error);
      Toast.fire({ icon: "error", title: "Системээс гарахад алдаа гарлаа." });
    }
  };

  if (authChecking) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-slate-50">
        <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-font-semibold tw-text-slate-500">
          <FiLoader className="tw-animate-spin" size={20} />
          Эрх шалгаж байна...
        </div>
      </div>
    );
  }

  if (myRole !== "superadmin") {
    const message = !currentUser
      ? "Нэвтэрсэн хэрэглэгч олдсонгүй. Нэвтрэлтийн төлөвөө шалгана уу."
      : authError
        ? "Хэрэглэгчийн эрхийн мэдээллийг уншихад алдаа гарлаа. Firestore Rules болон сүлжээний холболтыг шалгана уу."
        : profileMissing
          ? "users/{uid} замд хэрэглэгчийн profile бүртгэл олдсонгүй."
          : !isActive
            ? "Хэрэглэгчийн эрх идэвхгүй байна."
            : "Энэ хуудсанд зөвхөн Super Admin эрхтэй хэрэглэгч нэвтрэх боломжтой.";

    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-slate-50 tw-p-6">
        <div className="tw-w-full tw-max-w-md tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-8 tw-text-center tw-shadow-sm">
          <FiLock className="tw-mx-auto tw-mb-4 tw-text-slate-500" size={28} />
          <h2 className="tw-text-lg tw-font-bold tw-text-slate-900">Нэвтрэх эрх хүрэлцэхгүй байна</h2>
          <p className="tw-mt-3 tw-text-sm tw-leading-6 tw-text-slate-600">{message}</p>
          <button
            type="button"
            onClick={() => navigate(currentUser ? "/dashboard" : "/login")}
            className="tw-mt-5 tw-rounded-xl tw-bg-blue-600 tw-px-5 tw-py-3 tw-text-sm tw-font-semibold tw-text-white"
          >
            {currentUser ? "Самбар руу буцах" : "Нэвтрэх"}
          </button>
        </div>
      </div>
    );
  }

  const selectedParking = parkings.find(
    (parking) => parking.id === selectedParkingId
  );

  return (
    <div className="tw-min-h-screen tw-bg-[#F6F8FB] tw-font-sans tw-text-slate-800">
      <header className="tw-sticky tw-top-0 tw-z-40 tw-border-b tw-border-slate-200/70 tw-bg-white/85 tw-backdrop-blur-xl">
        <div className="tw-mx-auto tw-flex tw-max-w-7xl tw-items-center tw-justify-between tw-gap-4 tw-px-4 tw-py-3 sm:tw-px-6">
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
  
            <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-gradient-to-br tw-from-blue-600 tw-to-indigo-600 tw-text-white tw-shadow-lg tw-shadow-blue-600/20">
              <FiShield size={19} />
            </span>
            <div className="tw-min-w-0">
              <h1 className="tw-m-0 tw-truncate tw-text-base tw-font-bold tw-tracking-[-0.02em] tw-text-slate-950 sm:tw-text-[17px]">
                iParking · Зогсоолын удирдлага
              </h1>
              <p className="tw-m-0 tw-truncate tw-text-xs tw-text-slate-400">
                {selectedParking
                  ? selectedParking.name || selectedParking.id
                  : "Super Admin"}
                {dirty ? " · Хадгалаагүй өөрчлөлттэй" : ""}
              </p>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            {selectedParkingId && activeTab === "settings" && (
              <button
                type="button"
                onClick={saveParking}
                disabled={saving || !dirty}
                className="tw-hidden tw-h-10 tw-items-center tw-gap-2 tw-rounded-xl tw-bg-blue-600 tw-px-4 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-blue-600/20 tw-transition-all hover:tw-bg-blue-700 hover:tw-shadow-md disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300 disabled:tw-shadow-none sm:tw-flex"
              >
                {saving ? (
                  <FiLoader className="tw-animate-spin" />
                ) : (
                  <FiSave />
                )}
                Хадгалах
              </button>
            )}
            <button
              type="button"
              onClick={logout}
              className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-text-slate-500 tw-shadow-sm tw-transition-all hover:tw-border-slate-300 hover:tw-bg-slate-50 hover:tw-text-slate-900"
              title="Гарах"
            >
              <FiLogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="tw-mx-auto tw-max-w-7xl tw-px-3 tw-py-4 sm:tw-px-5 sm:tw-py-7 lg:tw-px-6">
        <nav className="tw-mb-5 tw-flex tw-gap-1 tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white tw-p-1.5 tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)] sm:tw-mb-6">
          {[
            { id: "parkings", label: "Зогсоол", icon: FiMapPin },
            { id: "settings", label: "Тохиргоо", icon: FiSettings },
            { id: "registeredCars", label: "Хэрэглэгчид", icon: FiUsers },
            { id: "extensionReport", label: "Сунгалт", icon: FiDollarSign },
            { id: "users", label: "Эрх", icon: FiShield },
          ].map((tab) => {
            const disabled = tab.id !== "parkings" && !selectedParkingId;
            const Icon = tab.icon;

            return (
              <button
                key={tab.id}
                type="button"
                disabled={disabled}
                onClick={() => changeTab(tab.id)}
                className={classNames(
                  "tw-flex tw-min-w-fit tw-flex-1 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-transition-all sm:tw-text-sm",
                  activeTab === tab.id
                    ? "tw-bg-slate-950 tw-text-white tw-shadow-sm"
                    : "tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-900",
                  disabled && "tw-cursor-not-allowed tw-opacity-35"
                )}
              >
                <Icon size={15} className="tw-shrink-0" />
                <span className="tw-whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
        </nav>

        {activeTab === "parkings" && (
          <div className="tw-space-y-5">
            <Card
              title="Зогсоол сонгох"
              description="Засварлах зогсоолоо сонгоно уу."
              icon={FiMapPin}
              action={
                myRole === "superadmin" ? (
                  <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    className="tw-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-xl tw-bg-blue-600 tw-px-4 tw-text-xs tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-blue-600/15 tw-transition-all hover:tw-bg-blue-700 hover:tw-shadow-md"
                  >
                    <FiPlus />
                    Шинэ зогсоол
                  </button>
                ) : null
              }
            >
              <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row">
                <div className="tw-relative tw-flex-1">
                  <FiSearch className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" />
                  <input
                    value={parkingSearch}
                    onChange={(event) => setParkingSearch(event.target.value)}
                    placeholder="Нэр, ID эсвэл хаягаар хайх"
                    className="tw-h-11 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-pl-10 tw-pr-3 tw-text-sm tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)] tw-outline-none tw-transition-all focus:tw-border-blue-400 focus:tw-ring-4 focus:tw-ring-blue-500/10"
                  />
                </div>
                <button
                  type="button"
                  onClick={loadParkings}
                  disabled={loadingParkings}
                  className="tw-flex tw-h-11 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-text-sm tw-font-semibold tw-text-slate-600 tw-shadow-sm tw-transition-all hover:tw-border-slate-300 hover:tw-bg-slate-50 disabled:tw-opacity-50"
                >
                  <FiRefreshCw
                    className={loadingParkings ? "tw-animate-spin" : ""}
                  />
                  Шинэчлэх
                </button>
              </div>

              {loadingParkings ? (
                <div className="tw-flex tw-h-40 tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-font-medium tw-text-slate-400">
                  <FiLoader className="tw-animate-spin" /> Уншиж байна...
                </div>
              ) : filteredParkings.length === 0 ? (
                <div className="tw-rounded-xl tw-bg-slate-50 tw-p-8 tw-text-center tw-text-sm tw-text-slate-400">
                  Зогсоол олдсонгүй.
                </div>
              ) : (
                <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2 xl:tw-grid-cols-3">
                  {filteredParkings.map((parking) => {
                    const active = selectedParkingId === parking.id;
                    return (
                      <button
                        key={parking.id}
                        type="button"
                        onClick={() => selectParking(parking.id)}
                        className={classNames(
                          "tw-group tw-rounded-2xl tw-border tw-p-4 tw-text-left tw-transition-all",
                          active
                            ? "tw-border-slate-400 tw-bg-slate-100/60 tw-ring-4 tw-ring-slate-200"
                            : "tw-border-slate-200 tw-bg-white hover:tw-border-slate-300 hover:"
                        )}
                      >
                        <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                          <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-slate-100 tw-text-slate-500 group-hover:tw-bg-slate-100 group-hover:tw-text-slate-700">
                            <FiMapPin size={18} />
                          </span>
                          {active && (
                            <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-slate-900 tw-text-white">
                              <FiCheck size={14} />
                            </span>
                          )}
                        </div>
                        <h3 className="tw-m-0 tw-mt-3 tw-text-sm tw-font-bold tw-text-slate-900">
                          {parking.name || parking.config?.name || parking.id}
                        </h3>
                        <p className="tw-m-0 tw-mt-1 tw-truncate tw-text-xs tw-text-slate-400">
                          ID: {parking.id}
                        </p>
                        <p className="tw-m-0 tw-mt-1 tw-truncate tw-text-xs tw-text-slate-400">
                          {parking.config?.address || "Хаяггүй"}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === "settings" &&
          (loadingParking ? (
            <div className="tw-flex tw-min-h-[360px] tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-400">
              <FiLoader className="tw-animate-spin" /> Мэдээлэл уншиж байна...
            </div>
          ) : !selectedParkingId ? (
            <EmptyParking />
          ) : (
            <div className="tw-space-y-5">
              <Card
                title="Үндсэн мэдээлэл"
                description="Нэр, код болон ажиллах горим."
                icon={FiEdit3}
              >
                <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                  <Field
                    label="Зогсоолын нэр"
                    name="name"
                    value={parkingData.root.name}
                    onChange={(event) => handleSectionChange("root", event)}
                  />
                  <Field
                    label="Үндсэн Join Code"
                    name="joinCode"
                    value={parkingData.root.joinCode}
                    onChange={(event) => handleSectionChange("root", event)}
                  />
                  <SelectField
                    label="Ажиллах горим"
                    name="status"
                    value={parkingData.config.status}
                    onChange={(event) => handleSectionChange("config", event)}
                  >
                    <option value="normal">Хэвийн</option>
                    <option value="firstPay">Урьдчилж төлөх</option>
                    <option value="onlyOwner">Зөвхөн бүртгэлтэй</option>
                    <option value="flatFee">Нэг удаагийн төлбөр</option>
                    <option value="service">Засвартай</option>
                    <option value="closed">Хаалттай</option>
                  </SelectField>
                  <Field
                    label="Хаяг"
                    name="address"
                    value={parkingData.config.address}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                  <Field
                    label="Энгийн мэндчилгээ"
                    name="note"
                    value={parkingData.config.note}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                  <Field
                    label="VIP мэндчилгээ"
                    name="vipNote"
                    value={parkingData.config.vipNote}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                </div>
              </Card>

              <Card
                title="Үнэ тариф"
                description="Зогсоолын төлбөр бодох үндсэн утгууд."
                icon={FiDollarSign}
              >
                <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
                  <SelectField
                    label="Төлбөрийн төрөл"
                    name="PAYMENT_TYPE"
                    value={parkingData.config.PAYMENT_TYPE}
                    onChange={(event) => handleSectionChange("config", event)}
                  >
                    <option value="hourly">Цагаар</option>
                    <option value="fixed">Тогтмол</option>
                  </SelectField>
                  <SelectField
                    label="Тооцоолох дүрэм"
                    name="chargeStrategy"
                    value={parkingData.config.chargeStrategy}
                    onChange={(event) => handleSectionChange("config", event)}
                  >
                    <option value="subtract">Үнэгүй хугацааг хасна</option>
                    <option value="fullTime">Нийт хугацаагаар</option>
                  </SelectField>
                  <Field
                    label="Үнэгүй минут"
                    name="FREE_MINUTES"
                    type="number"
                    value={parkingData.config.FREE_MINUTES}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                  <Field
                    label="Урилгатай зочны үнэгүй минут"
                    name="INVITED_FREE_MINUTES"
                    type="number"
                    value={parkingData.config.INVITED_FREE_MINUTES}
                    onChange={(event) => handleSectionChange("config", event)}
                    hint="Жишээ: 240 = 4 цаг. Энэ хугацаанаас хэтэрвэл hourly тарифаар бодно."
                  />
                  <Field
                    label="Цагийн үнэ"
                    name="PRICE_PER_HOUR"
                    type="number"
                    value={parkingData.config.PRICE_PER_HOUR}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                  <Field
                    label="Тогтмол үнэ"
                    name="FIXED_PRICE"
                    type="number"
                    value={parkingData.config.FIXED_PRICE}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                  <Field
                    label="Том машины цагийн үнэ"
                    name="CONFIG_PRICE_LARGE"
                    type="number"
                    value={parkingData.config.CONFIG_PRICE_LARGE}
                    onChange={(event) => handleSectionChange("config", event)}
                    hint="Автобус болон том оврын тээврийн хэрэгслийн цагийн тариф."
                  />
                  <Field
                    label="Том машины тогтмол үнэ"
                    name="FIXED_PRICE_LARGE"
                    type="number"
                    value={parkingData.config.FIXED_PRICE_LARGE}
                    onChange={(event) => handleSectionChange("config", event)}
                    hint="PAYMENT_TYPE=fixed үед том машинд ашиглах тариф."
                  />
                  <Field
                    label="Төлсний дараах минут"
                    name="PAYMENT_GRACE_MINUTES"
                    type="number"
                    value={parkingData.config.PAYMENT_GRACE_MINUTES}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                  <Field
                    label="Hourly Rate"
                    name="HOURLY_RATE"
                    type="number"
                    value={parkingData.config.HOURLY_RATE}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                  <Field
                    label="Суурь үнэ"
                    name="priceParking"
                    type="number"
                    value={parkingData.config.priceParking}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                </div>
              </Card>

              <Card
                title="Урилга ба зочин"
                description="Зочны урилга болон өдрийн лимит."
                icon={FiKey}
              >
                <div className="tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-2">
                  <Toggle
                    label="Дугаараар зочин урих"
                    description="Апп-аас машины дугаар оруулж урилга үүсгэнэ."
                    checked={parkingData.config.inviteNumber === true}
                    onChange={(value) =>
                      setBooleanValue("config", "inviteNumber", value)
                    }
                  />
                  <Toggle
                    label="Сул зогсоолын товч харуулах"
                    description="Апп дээр үлдсэн зогсоолын мэдээлэл харуулна."
                    checked={parkingData.config.buttonRemain === true}
                    onChange={(value) =>
                      setBooleanValue("config", "buttonRemain", value)
                    }
                  />
                </div>
                <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
                  <Field
                    label="Урилгын хүчинтэй цаг"
                    name="invitationNumerTime"
                    type="number"
                    value={parkingData.config.invitationNumerTime}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                  <Field
                    label="Кодын өдрийн лимит"
                    name="inviteCodeDailyLimit"
                    type="number"
                    value={parkingData.config.inviteCodeDailyLimit}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                  <Field
                    label="Дугаарын өдрийн лимит"
                    name="guestPlateDailyLimit"
                    type="number"
                    value={parkingData.config.guestPlateDailyLimit}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                  <Field
                    label="Сул зогсоолын босго"
                    name="buttonRemainValue"
                    type="number"
                    value={parkingData.config.buttonRemainValue}
                    onChange={(event) => handleSectionChange("config", event)}
                  />
                </div>
              </Card>
              <section className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white">
                <button
                  type="button"
                  onClick={() => setShowAdvanced((previous) => !previous)}
                  className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4 tw-px-5 tw-py-4 tw-text-left"
                >
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <span className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-xl tw-bg-slate-100 tw-text-slate-500">
                      <FiSettings size={17} />
                    </span>
                    <div>
                      <h2 className="tw-m-0 tw-text-sm tw-font-bold tw-text-slate-900">
                        Нэмэлт тохиргоо
                      </h2>
                      <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-slate-400">
                        API, и-баримт, GPS болон төхөөрөмж.
                      </p>
                    </div>
                  </div>
                  {showAdvanced ? <FiChevronUp /> : <FiChevronDown />}
                </button>

                {showAdvanced && (
                  <div className="tw-space-y-7 tw-border-t tw-border-slate-100 tw-p-5">
                    <div>
                      <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-slate-800">
                        <FiCreditCard className="tw-text-slate-700" /> Төлбөр ба API
                      </div>
                      <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
                        <Field
                          label="Merchant Code"
                          name="merchantCode"
                          value={parkingData.config.merchantCode}
                          onChange={(event) => handleSectionChange("config", event)}
                        />
                        <Field
                          label="API User"
                          name="apiUser"
                          value={parkingData.config.apiUser}
                          onChange={(event) => handleSectionChange("config", event)}
                        />
                        <Field
                          label="API Password"
                          name="apiPass"
                          type="password"
                          value={parkingData.config.apiPass}
                          onChange={(event) => handleSectionChange("config", event)}
                          autoComplete="new-password"
                        />
                        <Field
                          label="Config Join Code"
                          name="joinCode"
                          value={parkingData.config.joinCode}
                          onChange={(event) => handleSectionChange("config", event)}
                        />
                        <Field
                          label="Урьдчилсан цаг"
                          name="prepaidHours"
                          type="number"
                          value={parkingData.config.prepaidHours}
                          onChange={(event) => handleSectionChange("config", event)}
                        />
                        <Toggle
                          label="Mock / Test горим"
                          description="Бодит төлбөрийн гүйлгээгүй туршина."
                          checked={parkingData.config.isMockMode === true}
                          onChange={(value) =>
                            setBooleanValue("config", "isMockMode", value)
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-slate-800">
                        <FiLock className="tw-text-slate-700" /> И-Баримт
                      </div>
                      
                      <div className="tw-mb-4 tw-max-w-md">
                        <Toggle
                          label="И-Баримт олгох"
                          description="Төлбөр амжилттай үед НӨАТ-ын баримт үүсгэнэ."
                          checked={parkingData.config.ebarimt === true}
                          onChange={(value) =>
                            setBooleanValue(
                              "config",
                              "ebarimt",
                              value
                            )
                          }
                        />
                      </div>

                      {parkingData.config.ebarimt === true && (
                        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
                          <Field
                            label="Terminal No"
                            name="terminalNo"
                            value={parkingData.ebarimt.terminalNo}
                            onChange={(event) =>
                              handleSectionChange("ebarimt", event)
                            }
                          />

                          <p className="tw-text-xs tw-text-amber-700 md:tw-col-span-2">
                            И-Баримтын шифрлэлтийн түлхүүрийг Web тохиргоонд оруулахгүй.
                            Серверийн IPARKING_EBARIMT_TENANTS_JSON нууц орчинд тохируулна.
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-slate-800">
                        <FiMapPin className="tw-text-slate-700" /> Байршил
                      </div>
                      <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
                        <Field
                          label="Latitude"
                          name="lat"
                          type="number"
                          value={parkingData.config.lat}
                          onChange={(event) => handleSectionChange("config", event)}
                        />
                        <Field
                          label="Longitude"
                          name="lng"
                          type="number"
                          value={parkingData.config.lng}
                          onChange={(event) => handleSectionChange("config", event)}
                        />
                        <Field
                          label="Radius / метр"
                          name="radius"
                          type="number"
                          value={parkingData.config.radius}
                          onChange={(event) => handleSectionChange("config", event)}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-slate-800">
                        <FiZap className="tw-text-slate-700" /> Хаалга ба төхөөрөмж
                      </div>

                      <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
                        <Field
                          label="Gate Join Code"
                          name="joinCode"
                          value={parkingData.gate.joinCode}
                          onChange={(event) => handleSectionChange("gate", event)}
                        />

                        {gateCameraRegistry.length === 0 && (
                          <>
                            <button
                              type="button"
                              onClick={() => openGate("enterOpen")}
                              disabled={Boolean(gateLoading)}
                              className="tw-flex tw-h-11 tw-items-center tw-justify-center tw-gap-2 tw-self-end tw-rounded-xl tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-text-sm tw-font-semibold tw-text-emerald-700 tw-transition hover:tw-bg-emerald-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                            >
                              {gateLoading === "enter_1" ? (
                                <FiLoader className="tw-animate-spin" />
                              ) : (
                                <FiZap />
                              )}
                              Legacy орох нээх
                            </button>

                            <button
                              type="button"
                              onClick={() => openGate("exitOpen")}
                              disabled={Boolean(gateLoading)}
                              className="tw-flex tw-h-11 tw-items-center tw-justify-center tw-gap-2 tw-self-end tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-100 tw-text-sm tw-font-semibold tw-text-slate-700 tw-transition hover:tw-bg-slate-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                            >
                              {gateLoading === "exit_1" ? (
                                <FiLoader className="tw-animate-spin" />
                              ) : (
                                <FiZap />
                              )}
                              Legacy гарах нээх
                            </button>
                          </>
                        )}
                      </div>

                      <div className="tw-mt-5 tw-rounded-2xl tw-border tw-border-blue-100 tw-bg-blue-50/60 tw-p-4 sm:tw-p-5">
                        <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">
                          <div>
                            <h3 className="tw-m-0 tw-text-sm tw-font-bold tw-text-slate-900">
                              Камерын тоо
                            </h3>
                            <p className="tw-m-0 tw-mt-1 tw-text-xs tw-leading-5 tw-text-slate-500">
                              Зогсоол үүссэний дараа ENTER / EXIT камерын төлөвлөсөн тоог эндээс өөрчилнө.
                            </p>
                          </div>
                          <span className="tw-inline-flex tw-h-7 tw-items-center tw-self-start tw-rounded-full tw-border tw-border-blue-100 tw-bg-white tw-px-3 tw-text-[11px] tw-font-semibold tw-text-blue-700">
                            Нийт {Number(currentCameraPlan.totalCount || 0)}
                          </span>
                        </div>

                        <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                          <Field
                            label="Орох камерын тоо"
                            name="settingsEnterCameraCount"
                            type="number"
                            value={parkingData.gate?.cameraPlan?.enterCount ?? 0}
                            onChange={(event) =>
                              updateCameraPlanCount(
                                "enter",
                                event.target.value
                              )
                            }
                            hint="0–32"
                          />

                          <Field
                            label="Гарах камерын тоо"
                            name="settingsExitCameraCount"
                            type="number"
                            value={parkingData.gate?.cameraPlan?.exitCount ?? 0}
                            onChange={(event) =>
                              updateCameraPlanCount(
                                "exit",
                                event.target.value
                              )
                            }
                            hint="0–32"
                          />
                        </div>

                        <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2">
                          <span className="tw-inline-flex tw-rounded-full tw-bg-emerald-100 tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-emerald-700">
                            Төлөвлөгөө ENTER: {Number(currentCameraPlan.enterCount || 0)}
                          </span>
                          <span className="tw-inline-flex tw-rounded-full tw-bg-blue-100 tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-blue-700">
                            Төлөвлөгөө EXIT: {Number(currentCameraPlan.exitCount || 0)}
                          </span>
                          <span className="tw-inline-flex tw-rounded-full tw-bg-white tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-slate-600 tw-ring-1 tw-ring-slate-200">
                            Local ENTER: {actualConfiguredCounts.enter}
                          </span>
                          <span className="tw-inline-flex tw-rounded-full tw-bg-white tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-slate-600 tw-ring-1 tw-ring-slate-200">
                            Local EXIT: {actualConfiguredCounts.exit}
                          </span>
                        </div>

                        {(Number(currentCameraPlan.enterCount || 0) !==
                          actualConfiguredCounts.enter ||
                          Number(currentCameraPlan.exitCount || 0) !==
                            actualConfiguredCounts.exit) && (
                          <div className="tw-mt-4 tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-3 tw-text-xs tw-leading-5 tw-text-amber-800">
                            Firestore дээрх төлөвлөсөн камерын тоо local parking server-ийн бодит camera registry-тэй зөрж байна.
                            Шинээр нэмсэн physical camera-г local server-ийн Camera Settings дээр IP / vendor / credential-тай нь тохируулсны дараа тухайн хаалганы OPEN command идэвхтэй ажиллана.
                          </div>
                        )}
                      </div>

                      <div className="tw-mt-5 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50/70 tw-p-4 sm:tw-p-5">
                        <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">
                          <div>
                            <h3 className="tw-m-0 tw-text-sm tw-font-bold tw-text-slate-900">
                              Dashboard хаалганы товч
                            </h3>
                            <p className="tw-m-0 tw-mt-1 tw-text-xs tw-leading-5 tw-text-slate-500">
                              Local server-ээс ирсэн camera registry дээр тулгуурлан Dashboard-д харагдах нэр, дараалал болон харагдах эсэхийг тохируулна.
                            </p>
                          </div>
                          <span className="tw-inline-flex tw-h-7 tw-items-center tw-self-start tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-[11px] tw-font-semibold tw-text-slate-600">
                            {plannedGateCameraRows.length} товч
                          </span>
                        </div>

                        {plannedGateCameraRows.length === 0 ? (
                          <div className="tw-mt-4 tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-4 tw-text-xs tw-leading-5 tw-text-amber-800">
                            Camera registry хараахан Firestore-д ирээгүй байна. Parking server / remote_config ажилласны дараа
                            <span className="tw-font-bold"> gate.cameras </span>
                            автоматаар үүсэж, энд камер бүрийн Dashboard товчны тохиргоо гарна.
                          </div>
                        ) : (
                          <div className="tw-mt-4 tw-space-y-3">
                            {dashboardButtonRows.map((camera) => {
                              const isExit = camera.role === "exit";

                              return (
                                <div
                                  key={camera.camera_id}
                                  className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-shadow-sm"
                                >
                                  <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-start lg:tw-justify-between">
                                    <div className="tw-min-w-0">
                                      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                        <span
                                          className={classNames(
                                            "tw-inline-flex tw-h-6 tw-items-center tw-rounded-full tw-px-2.5 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide",
                                            isExit
                                              ? "tw-bg-blue-50 tw-text-blue-700 tw-ring-1 tw-ring-blue-100"
                                              : "tw-bg-emerald-50 tw-text-emerald-700 tw-ring-1 tw-ring-emerald-100"
                                          )}
                                        >
                                          {isExit ? "EXIT" : "ENTER"}
                                        </span>
                                        <span className="tw-font-mono tw-text-xs tw-font-semibold tw-text-slate-700">
                                          {camera.camera_id}
                                        </span>
                                        {camera.vendor && (
                                          <span className="tw-text-[10px] tw-uppercase tw-text-slate-400">
                                            {camera.vendor}
                                          </span>
                                        )}
                                        {!camera.enabled && (
                                          <span className="tw-text-[10px] tw-font-semibold tw-text-rose-500">
                                            DISABLED
                                          </span>
                                        )}
                                      </div>
                                      <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-slate-400">
                                        Төхөөрөмжийн нэр: {camera.name || camera.camera_id}
                                      </p>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() => openGate(camera)}
                                      disabled={
                                        Boolean(gateLoading) ||
                                        !camera.enabled ||
                                        camera.configured === false
                                      }
                                      className={classNames(
                                        "tw-flex tw-h-9 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-px-3 tw-text-xs tw-font-semibold tw-transition disabled:tw-cursor-not-allowed disabled:tw-opacity-50",
                                        isExit
                                          ? "tw-border-blue-200 tw-bg-blue-50 tw-text-blue-700 hover:tw-bg-blue-100"
                                          : "tw-border-emerald-200 tw-bg-emerald-50 tw-text-emerald-700 hover:tw-bg-emerald-100"
                                      )}
                                    >
                                      {gateLoading === camera.camera_id ? (
                                        <FiLoader className="tw-animate-spin" />
                                      ) : (
                                        <FiZap />
                                      )}
                                      Туршиж нээх
                                    </button>
                                  </div>

                                  <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-[minmax(0,1fr)_120px_180px]">
                                    <Field
                                      label="Dashboard дээрх товчны нэр"
                                      name={`dashboardLabel_${camera.camera_id}`}
                                      value={camera.dashboardLabel}
                                      onChange={(event) =>
                                        updateDashboardButton(
                                          camera.camera_id,
                                          "label",
                                          event.target.value
                                        )
                                      }
                                      placeholder={camera.name || camera.camera_id}
                                      hint="Жишээ: Гарах хаалга 2"
                                    />

                                    <Field
                                      label="Дараалал"
                                      name={`dashboardOrder_${camera.camera_id}`}
                                      type="number"
                                      value={camera.dashboardOrder}
                                      onChange={(event) =>
                                        updateDashboardButton(
                                          camera.camera_id,
                                          "order",
                                          event.target.value
                                        )
                                      }
                                      hint="0, 1, 2..."
                                    />

                                    <Toggle
                                      label="Dashboard-д харуулах"
                                      description={
                                        camera.dashboardVisible
                                          ? "Товч харагдана"
                                          : "Товч нуугдана"
                                      }
                                      checked={camera.dashboardVisible}
                                      onChange={(value) =>
                                        updateDashboardButton(
                                          camera.camera_id,
                                          "visible",
                                          value
                                        )
                                      }
                                    />
                                  </div>

                                  <div className="tw-mt-3 tw-flex tw-justify-end">
                                    <button
                                      type="button"
                                      onClick={() => resetDashboardButton(camera)}
                                      className="tw-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-lg tw-px-2.5 tw-text-[11px] tw-font-semibold tw-text-slate-500 tw-transition hover:tw-bg-slate-100 hover:tw-text-slate-800"
                                    >
                                      <FiRefreshCw size={13} />
                                      Default болгох
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </section>

              <div className="tw-sticky tw-bottom-3 tw-z-20 tw-rounded-2xl tw-border tw-border-white/70 tw-bg-white/80 tw-p-2 tw-shadow-xl tw-shadow-slate-900/10 tw-backdrop-blur-xl sm:tw-hidden">
                <button
                  type="button"
                  onClick={saveParking}
                  disabled={saving || !dirty}
                  className="tw-flex tw-h-11 tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-bg-blue-600 tw-px-4 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-blue-600/20 disabled:tw-bg-slate-300 disabled:tw-shadow-none"
                >
                  {saving ? (
                    <FiLoader className="tw-animate-spin" />
                  ) : (
                    <FiSave />
                  )}
                  Хадгалах
                </button>
              </div>
            </div>
          ))}


        {activeTab === "registeredCars" &&
          (!selectedParkingId ? (
            <EmptyParking />
          ) : (
            <div className="tw-space-y-5">
              <div className="tw-grid tw-grid-cols-3 tw-gap-2 sm:tw-gap-3">
                {[
                  { label: "Нийт", value: registeredCarStats.total, valueClass: "tw-text-slate-900" },
                  { label: "Хүчинтэй", value: registeredCarStats.active, valueClass: "tw-text-emerald-700" },
                  { label: "Дууссан", value: registeredCarStats.expired, valueClass: "tw-text-rose-700" },
                ].map((item) => (
                  <div key={item.label} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white tw-p-3.5 tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)] sm:tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-slate-500 sm:tw-text-xs">{item.label}</div>
                    <div className={classNames("tw-mt-1 tw-text-xl tw-font-semibold sm:tw-text-2xl", item.valueClass)}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              <Card
                title="Бүртгэлтэй хэрэглэгчдийн хугацаа"
                description={`${selectedParking?.name || selectedParking?.config?.name || selectedParkingId} зогсоолын хэрэглэгчид`}
                icon={FiCalendar}
                action={
                  <button
                    type="button"
                    onClick={loadRegisteredCars}
                    disabled={loadingRegisteredCars || adjustingExpiry}
                    className="tw-flex tw-h-9 tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-text-xs tw-font-semibold tw-text-slate-600 tw-shadow-sm tw-transition-all hover:tw-border-slate-300 hover:tw-bg-slate-50 disabled:tw-opacity-50"
                  >
                    <FiRefreshCw
                      className={loadingRegisteredCars ? "tw-animate-spin" : ""}
                    />
                    Шинэчлэх
                  </button>
                }
              >
                <div className="tw-mb-5 tw-grid tw-grid-cols-1 tw-gap-3 lg:tw-grid-cols-[minmax(0,1fr)_auto]">
                  <div className="tw-relative">
                    <FiSearch className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" />
                    <input
                      value={registeredCarSearch}
                      onChange={(event) => setRegisteredCarSearch(event.target.value)}
                      placeholder="Дугаар, нэр, утас, байр, тоотоор хайх"
                      className="tw-h-11 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-pl-10 tw-pr-3 tw-text-sm tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)] tw-outline-none tw-transition-all focus:tw-border-blue-400 focus:tw-ring-4 focus:tw-ring-blue-500/10"
                    />
                  </div>

                  <div className="tw-flex tw-overflow-x-auto tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-100/70 tw-p-1">
                    {[
                      ["all", `Бүгд ${registeredCarStats.total}`],
                      ["active", `Хүчинтэй ${registeredCarStats.active}`],
                      ["expired", `Дууссан ${registeredCarStats.expired}`],
                    ].map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setRegisteredCarFilter(id)}
                        className={classNames(
                          "tw-whitespace-nowrap tw-rounded-lg tw-px-3.5 tw-py-2 tw-text-xs tw-font-bold tw-transition",
                          registeredCarFilter === id
                            ? "tw-bg-white tw-text-slate-950 tw-shadow-sm"
                            : "tw-text-slate-500 hover:tw-text-slate-800"
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="tw-mb-5 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50/70 tw-p-4">
                  <div className="tw-flex tw-flex-col tw-gap-4 xl:tw-flex-row xl:tw-items-end xl:tw-justify-between">
                    <div>
                      <div className="tw-text-sm tw-font-semibold tw-text-slate-900">Бөөнөөр хугацаа өөрчлөх</div>
                      <p className="tw-m-0 tw-mt-1 tw-text-xs tw-leading-5 tw-text-slate-500">
                        Сонгосон хэрэглэгчид эсвэл одоогийн шүүлтүүрт харагдаж буй бүх хэрэглэгчид хоног нэмэх/хасах боломжтой.
                      </p>
                    </div>

                    <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-end">
                      <label>
                        <span className="tw-mb-1 tw-block tw-text-[11px] tw-font-bold tw-text-slate-500">
                          Хамрах хүрээ
                        </span>
                        <select
                          value={bulkExpiryScope}
                          onChange={(event) => setBulkExpiryScope(event.target.value)}
                          disabled={adjustingExpiry}
                          className="tw-h-10 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-xs tw-font-bold tw-text-slate-700 tw-outline-none"
                        >
                          <option value="selected">
                            Сонгосон ({selectedRegisteredCarIds.length})
                          </option>
                          <option value="filtered">
                            Шүүлтүүрийн бүх ({filteredRegisteredCars.length})
                          </option>
                        </select>
                      </label>

                      <label>
                        <span className="tw-mb-1 tw-block tw-text-[11px] tw-font-bold tw-text-slate-500">
                          Хоног
                        </span>
                        <input
                          type="number"
                          min="1"
                          max="3650"
                          step="1"
                          value={bulkExpiryDays}
                          onChange={(event) => setBulkExpiryDays(event.target.value)}
                          disabled={adjustingExpiry}
                          className="tw-h-10 tw-w-28 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-sm tw-font-bold tw-text-slate-900 tw-outline-none"
                        />
                      </label>

                      <label>
                        <span className="tw-mb-1 tw-block tw-text-[11px] tw-font-bold tw-text-slate-500">
                          1 хэрэглэгчийн төлбөр
                        </span>
                        <div className="tw-relative">
                          <input
                            type="number"
                            min="0"
                            step="100"
                            value={bulkExpiryAmount}
                            onChange={(event) => setBulkExpiryAmount(event.target.value)}
                            disabled={adjustingExpiry}
                            className="tw-h-10 tw-w-36 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-pr-8 tw-text-sm tw-font-bold tw-text-slate-900 tw-outline-none"
                          />
                          <span className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-xs tw-font-bold tw-text-slate-400">
                            ₮
                          </span>
                        </div>
                      </label>

                      <button
                        type="button"
                        onClick={() => handleBulkExpiryAdjustment("subtract")}
                        disabled={
                          adjustingExpiry ||
                          (bulkExpiryScope === "selected" &&
                            selectedRegisteredCarIds.length === 0) ||
                          (bulkExpiryScope === "filtered" &&
                            filteredRegisteredCars.length === 0)
                        }
                        className="tw-flex tw-h-10 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-rose-200 tw-bg-white tw-px-4 tw-text-xs tw-font-semibold tw-text-rose-600 tw-transition hover:tw-bg-rose-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
                      >
                        {adjustingExpiry ? (
                          <FiLoader className="tw-animate-spin" />
                        ) : (
                          <FiMinus />
                        )}
                        Хасах
                      </button>

                      <button
                        type="button"
                        onClick={() => handleBulkExpiryAdjustment("add")}
                        disabled={
                          adjustingExpiry ||
                          (bulkExpiryScope === "selected" &&
                            selectedRegisteredCarIds.length === 0) ||
                          (bulkExpiryScope === "filtered" &&
                            filteredRegisteredCars.length === 0)
                        }
                        className="tw-flex tw-h-10 tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-bg-slate-900 tw-px-4 tw-text-xs tw-font-semibold tw-text-white tw-transition hover:tw-bg-slate-800 disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300"
                      >
                        {adjustingExpiry ? (
                          <FiLoader className="tw-animate-spin" />
                        ) : (
                          <FiPlus />
                        )}
                        Нэмэх
                      </button>
                    </div>
                  </div>
                </div>

                {loadingRegisteredCars ? (
                  <div className="tw-flex tw-min-h-[320px] tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-400">
                    <FiLoader className="tw-animate-spin" /> Хэрэглэгчид уншиж байна...
                  </div>
                ) : filteredRegisteredCars.length === 0 ? (
                  <div className="tw-flex tw-min-h-[280px] tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-dashed tw-border-slate-200 tw-bg-slate-50 tw-p-8 tw-text-center">
                    <FiUsers size={28} className="tw-text-slate-300" />
                    <div className="tw-mt-3 tw-text-sm tw-font-bold tw-text-slate-600">
                      Хэрэглэгч олдсонгүй
                    </div>
                    <div className="tw-mt-1 tw-text-xs tw-text-slate-400">
                      Хайлт эсвэл төлөвийн шүүлтүүрээ өөрчилнө үү.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="tw-space-y-3 md:tw-hidden">
                      {filteredRegisteredCars.map((car) => {
                        const expireDate = getRegisteredCarExpireDate(car);
                        const status = getRegisteredCarStatus(car);
                        const isActive = status === "active";
                        const isSelected = selectedRegisteredCarIds.includes(car.id);
                        const rowDays = rowExpiryDays[car.id] || "30";
                        const rowAmount = rowExpiryAmounts[car.id] || "0";
                        const location = [
                          car.building || car.address,
                          car.number || car.apartment
                            ? `${car.number || car.apartment} тоот`
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" · ");
                        const displayName = car.userName || car.name || "Нэргүй хэрэглэгч";
                        const displayPlate = car.plate || car.id || "-";
                        const isProcessing = processingRegisteredCarId === car.id;

                        return (
                          <article
                            key={car.id}
                            className={classNames(
                              "tw-rounded-xl tw-border tw-bg-white tw-p-4",
                              isSelected ? "tw-border-slate-400" : "tw-border-slate-200"
                            )}
                          >
                            <div className="tw-flex tw-items-start tw-gap-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleRegisteredCarSelection(car.id)}
                                className="tw-mt-1 tw-h-4 tw-w-4 tw-rounded tw-border-slate-300"
                                aria-label={`${displayPlate} сонгох`}
                              />
                              <div className="tw-min-w-0 tw-flex-1">
                                <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                                  <div className="tw-min-w-0">
                                    <div className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900">
                                      {displayName}
                                    </div>
                                    <div className="tw-mt-0.5 tw-text-xs tw-text-slate-500">
                                      {car.phone || "Утасгүй"}
                                    </div>
                                  </div>
                                  <span
                                    className={classNames(
                                      "tw-shrink-0 tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold",
                                      isActive
                                        ? "tw-bg-emerald-50 tw-text-emerald-700"
                                        : "tw-bg-rose-50 tw-text-rose-700"
                                    )}
                                  >
                                    {isActive ? "Хүчинтэй" : "Дууссан"}
                                  </span>
                                </div>

                                <div className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-2 tw-text-xs">
                                  <div>
                                    <div className="tw-text-slate-400">Улсын дугаар</div>
                                    <div className="tw-mt-0.5 tw-font-semibold tw-tracking-wider tw-text-slate-900">
                                      {displayPlate}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="tw-text-slate-400">Байр / Тоот</div>
                                    <div className="tw-mt-0.5 tw-font-medium tw-text-slate-700">
                                      {location || "-"}
                                    </div>
                                  </div>
                                  <div className="tw-col-span-2">
                                    <div className="tw-text-slate-400">Дуусах хугацаа</div>
                                    <div className="tw-mt-0.5 tw-font-medium tw-text-slate-800">
                                      {expireDate
                                        ? `${expireDate.toLocaleDateString("mn-MN")} ${expireDate.toLocaleTimeString("mn-MN", { hour: "2-digit", minute: "2-digit" })}`
                                        : "Хугацаа байхгүй"}
                                    </div>
                                  </div>
                                </div>

                                {car.isBlocked === true && (
                                  <div className="tw-mt-2 tw-text-xs tw-font-medium tw-text-rose-600">
                                    Блоклогдсон
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-2">
                              <label>
                                <span className="tw-mb-1 tw-block tw-text-[11px] tw-text-slate-500">Хоног</span>
                                <input
                                  type="number"
                                  min="1"
                                  max="3650"
                                  step="1"
                                  value={rowDays}
                                  onChange={(event) =>
                                    setRowExpiryDays((previous) => ({
                                      ...previous,
                                      [car.id]: event.target.value,
                                    }))
                                  }
                                  disabled={adjustingExpiry}
                                  className="tw-h-10 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-slate-900 tw-outline-none focus:tw-border-slate-400 focus:tw-ring-2 focus:tw-ring-slate-200"
                                />
                              </label>
                              <label>
                                <span className="tw-mb-1 tw-block tw-text-[11px] tw-text-slate-500">Төлбөр</span>
                                <div className="tw-relative">
                                  <input
                                    type="number"
                                    min="0"
                                    step="100"
                                    value={rowAmount}
                                    onChange={(event) =>
                                      setRowExpiryAmounts((previous) => ({
                                        ...previous,
                                        [car.id]: event.target.value,
                                      }))
                                    }
                                    disabled={adjustingExpiry}
                                    className="tw-h-10 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-pr-8 tw-text-sm tw-font-medium tw-text-slate-900 tw-outline-none focus:tw-border-slate-400 focus:tw-ring-2 focus:tw-ring-slate-200"
                                  />
                                  <span className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-xs tw-text-slate-400">₮</span>
                                </div>
                              </label>
                            </div>

                            <div className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-2">
                              <button
                                type="button"
                                onClick={() => handleSingleExpiryAdjustment(car, "subtract")}
                                disabled={adjustingExpiry || !expireDate}
                                className="tw-flex tw-h-10 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-text-sm tw-font-semibold tw-text-slate-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-35"
                              >
                                {isProcessing ? <FiLoader className="tw-animate-spin" /> : <FiMinus />}
                                Хасах
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSingleExpiryAdjustment(car, "add")}
                                disabled={adjustingExpiry}
                                className="tw-flex tw-h-10 tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-bg-slate-900 tw-text-sm tw-font-semibold tw-text-white disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300"
                              >
                                {isProcessing ? <FiLoader className="tw-animate-spin" /> : <FiPlus />}
                                Нэмэх
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>

                    <div className="tw-hidden tw-overflow-x-auto tw-rounded-xl tw-border tw-border-slate-200 md:tw-block">
                    <table className="tw-w-full tw-min-w-[1120px] tw-border-collapse tw-bg-white tw-text-left">
                      <thead className="tw-bg-slate-50">
                        <tr className="tw-border-b tw-border-slate-200">
                          <th className="tw-w-12 tw-px-4 tw-py-3.5">
                            <input
                              type="checkbox"
                              checked={allFilteredRegisteredCarsSelected}
                              onChange={toggleAllFilteredRegisteredCars}
                              className="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300"
                              aria-label="Харагдаж буй бүх хэрэглэгчийг сонгох"
                            />
                          </th>
                          <th className="tw-px-4 tw-py-3.5 tw-text-[11px] tw-font-semibold  tw-text-slate-500">
                            Хэрэглэгч
                          </th>
                          <th className="tw-px-4 tw-py-3.5 tw-text-[11px] tw-font-semibold  tw-text-slate-500">
                            Улсын дугаар
                          </th>
                          <th className="tw-px-4 tw-py-3.5 tw-text-[11px] tw-font-semibold  tw-text-slate-500">
                            Байр / Тоот
                          </th>
                          <th className="tw-px-4 tw-py-3.5 tw-text-[11px] tw-font-semibold  tw-text-slate-500">
                            Дуусах огноо
                          </th>
                          <th className="tw-px-4 tw-py-3.5 tw-text-[11px] tw-font-semibold  tw-text-slate-500">
                            Төлөв
                          </th>
                          <th className="tw-px-4 tw-py-3.5 tw-text-right tw-text-[11px] tw-font-semibold  tw-text-slate-500">
                            Хугацаа өөрчлөх
                          </th>
                        </tr>
                      </thead>
                      <tbody className="tw-divide-y tw-divide-slate-100">
                        {filteredRegisteredCars.map((car) => {
                          const expireDate = getRegisteredCarExpireDate(car);
                          const status = getRegisteredCarStatus(car);
                          const isActive = status === "active";
                          const isSelected = selectedRegisteredCarIds.includes(car.id);
                          const rowDays = rowExpiryDays[car.id] || "30";
                          const rowAmount = rowExpiryAmounts[car.id] || "0";
                          const location = [
                            car.building || car.address,
                            car.number || car.apartment
                              ? `${car.number || car.apartment} тоот`
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" · ");
                          const displayName = car.userName || car.name || "Нэргүй хэрэглэгч";
                          const displayPlate = car.plate || car.id || "-";
                          const isProcessing = processingRegisteredCarId === car.id;

                          return (
                            <tr
                              key={car.id}
                              className={classNames(
                                "tw-transition hover:tw-bg-slate-50/80",
                                isSelected && "tw-bg-slate-100/40"
                              )}
                            >
                              <td className="tw-px-4 tw-py-4">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleRegisteredCarSelection(car.id)}
                                  className="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300"
                                  aria-label={`${displayPlate} сонгох`}
                                />
                              </td>
                              <td className="tw-px-4 tw-py-4">
                                <div className="tw-text-sm tw-font-bold tw-text-slate-900">
                                  {displayName}
                                </div>
                                <div className="tw-mt-1 tw-text-[11px] tw-text-slate-400">
                                  {car.phone || "Утасгүй"}
                                </div>
                              </td>
                              <td className="tw-px-4 tw-py-4">
                                <span className="tw-inline-flex tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-semibold tw-tracking-widest tw-text-slate-800">
                                  {displayPlate}
                                </span>
                              </td>
                              <td className="tw-px-4 tw-py-4 tw-text-xs tw-font-semibold tw-text-slate-600">
                                {location || "-"}
                              </td>
                              <td className="tw-px-4 tw-py-4">
                                <div className="tw-text-sm tw-font-bold tw-text-slate-800">
                                  {expireDate
                                    ? expireDate.toLocaleDateString("mn-MN")
                                    : "Хугацаа байхгүй"}
                                </div>
                                {expireDate && (
                                  <div className="tw-mt-1 tw-text-[11px] tw-text-slate-400">
                                    {expireDate.toLocaleTimeString("mn-MN", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </div>
                                )}
                              </td>
                              <td className="tw-px-4 tw-py-4">
                                <span
                                  className={classNames(
                                    "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold",
                                    isActive
                                      ? "tw-bg-emerald-50 tw-text-emerald-700"
                                      : "tw-bg-rose-50 tw-text-rose-700"
                                  )}
                                >
                                  <span
                                    className={classNames(
                                      "tw-h-1.5 tw-w-1.5 tw-rounded-full",
                                      isActive ? "tw-bg-emerald-500" : "tw-bg-rose-500"
                                    )}
                                  />
                                  {isActive ? "Хүчинтэй" : "Хугацаа дууссан"}
                                </span>
                                {car.isBlocked === true && (
                                  <div className="tw-mt-1.5 tw-text-[10px] tw-font-bold tw-text-rose-500">
                                    Блоклогдсон
                                  </div>
                                )}
                              </td>
                              <td className="tw-px-4 tw-py-4">
                                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                  <input
                                    type="number"
                                    min="1"
                                    max="3650"
                                    step="1"
                                    value={rowDays}
                                    onChange={(event) =>
                                      setRowExpiryDays((previous) => ({
                                        ...previous,
                                        [car.id]: event.target.value,
                                      }))
                                    }
                                    disabled={adjustingExpiry}
                                    className="tw-h-9 tw-w-20 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-text-center tw-text-xs tw-font-semibold tw-text-slate-800 tw-outline-none focus:tw-border-slate-400"
                                  />
                                  <div className="tw-relative">
                                    <input
                                      type="number"
                                      min="0"
                                      step="100"
                                      value={rowAmount}
                                      onChange={(event) =>
                                        setRowExpiryAmounts((previous) => ({
                                          ...previous,
                                          [car.id]: event.target.value,
                                        }))
                                      }
                                      disabled={adjustingExpiry}
                                      title="Сунгалтын төлбөр"
                                      className="tw-h-9 tw-w-24 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-pr-6 tw-text-right tw-text-xs tw-font-bold tw-text-slate-800 tw-outline-none focus:tw-border-slate-400"
                                    />
                                    <span className="tw-pointer-events-none tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-text-[10px] tw-font-bold tw-text-slate-400">
                                      ₮
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleSingleExpiryAdjustment(car, "subtract")}
                                    disabled={adjustingExpiry || !expireDate}
                                    title={!expireDate ? "Хугацаа байхгүй тул хасах боломжгүй" : "Хоног хасах"}
                                    className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-rose-200 tw-bg-white tw-text-rose-600 tw-transition hover:tw-bg-rose-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-35"
                                  >
                                    {isProcessing ? (
                                      <FiLoader className="tw-animate-spin" />
                                    ) : (
                                      <FiMinus />
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSingleExpiryAdjustment(car, "add")}
                                    disabled={adjustingExpiry}
                                    title="Хоног нэмэх"
                                    className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-bg-slate-900 tw-text-white tw-transition hover:tw-bg-slate-800 disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300"
                                  >
                                    {isProcessing ? (
                                      <FiLoader className="tw-animate-spin" />
                                    ) : (
                                      <FiPlus />
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}

                <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-2 tw-text-[11px] tw-text-slate-400 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
                  <span>
                    {filteredRegisteredCars.length} хэрэглэгч харагдаж байна · {selectedRegisteredCarIds.length} сонгосон
                  </span>
                  <span>
                    Хугацаа нэмэхэд дууссан хэрэглэгч өнөөдрөөс эхэлнэ; хасахад одоогийн expireDate-оос тооцно.
                  </span>
                </div>
              </Card>
            </div>
          ))}

        {activeTab === "extensionReport" &&
          (!selectedParkingId ? (
            <EmptyParking />
          ) : (
            <div className="tw-space-y-5">
              <div className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-gap-3 lg:tw-grid-cols-4">
                {[
                  { label: "Сунгалт хийсэн", value: extensionReportStats.people, note: "Нийт" },
                  { label: "Амжилттай", value: extensionReportStats.transactions, note: `Нийт ${extensionReportStats.allTransactions}` },
                  { label: "Нийт сар", value: `${extensionReportStats.totalMonths} сар`, note: `${extensionReportStats.otherTransactions} бусад` },
                  { label: "Нийт орлого", value: formatCurrency(extensionReportStats.totalAmount), note: "Амжилттай гүйлгээ" },
                ].map((item) => (
                  <div key={item.label} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white tw-p-3.5 tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)] sm:tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-slate-500 sm:tw-text-xs">{item.label}</div>
                    <div className="tw-mt-1 tw-text-lg tw-font-semibold tw-text-slate-900 sm:tw-text-xl">{item.value}</div>
                    <div className="tw-mt-1 tw-text-[10px] tw-text-slate-400 sm:tw-text-[11px]">{item.note}</div>
                  </div>
                ))}
              </div>

              <Card
                title={`${selectedParking?.name || selectedParking?.config?.name || selectedParkingId}`}
                action={
                  <button
                    type="button"
                    onClick={loadExtensionReport}
                    disabled={loadingExtensionReport}
                    className="tw-flex tw-h-9 tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-xs tw-font-bold tw-text-slate-600 tw-transition hover:tw-bg-slate-50 disabled:tw-opacity-50"
                  >
                    <FiRefreshCw
                      className={loadingExtensionReport ? "tw-animate-spin" : ""}
                    />
                    Шинэчлэх
                  </button>
                }
              >
                <div className="tw-mb-5 tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-end lg:tw-justify-between">
                  <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row">
                    <label className="tw-w-full sm:tw-w-56">
                      <span className="tw-mb-1.5 tw-block tw-text-[11px] tw-font-semibold  tw-text-slate-500">
                        Огноо
                      </span>
                      <input
                        type="date"
                        value={extensionReportDate}
                        onChange={(event) =>
                          setExtensionReportDate(event.target.value)
                        }
                        className="tw-h-11 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-text-sm tw-font-semibold tw-text-slate-800 tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)] tw-outline-none tw-transition-all focus:tw-border-blue-400 focus:tw-ring-4 focus:tw-ring-blue-500/10"
                      />
                    </label>

                    <label className="tw-w-full sm:tw-w-48">
                      <span className="tw-mb-1.5 tw-block tw-text-[11px] tw-font-semibold  tw-text-slate-500">
                        Төлөв
                      </span>
                      <select
                        value={renewalStatusFilter}
                        onChange={(event) =>
                          setRenewalStatusFilter(event.target.value)
                        }
                        className="tw-h-11 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-text-sm tw-font-semibold tw-text-slate-800 tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)] tw-outline-none tw-transition-all focus:tw-border-blue-400 focus:tw-ring-4 focus:tw-ring-blue-500/10"
                      >
                        <option value="success">Амжилттай</option>
                        <option value="all">Бүх гүйлгээ</option>
                      </select>
                    </label>
                  </div>

                  <div className="tw-text-xs tw-leading-5 tw-text-slate-500">
                    Эх үүсвэр: <span className="tw-font-medium tw-text-slate-700">renewals</span> · KPI-д зөвхөн амжилттай гүйлгээ орно.
                  </div>
                </div>

                {loadingExtensionReport ? (
                  <div className="tw-flex tw-min-h-[320px] tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-400">
                    <FiLoader className="tw-animate-spin" /> Тайлан уншиж байна...
                  </div>
                ) : visibleRenewalLogs.length === 0 ? (
                  <div className="tw-flex tw-min-h-[280px] tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-dashed tw-border-slate-200 tw-bg-slate-50 tw-p-8 tw-text-center">
                    <FiDollarSign size={28} className="tw-text-slate-300" />
                    <div className="tw-mt-3 tw-text-sm tw-font-bold tw-text-slate-600">
                      Энэ өдөр сунгалтын мэдээлэл алга
                    </div>
                    <div className="tw-mt-1 tw-text-xs tw-text-slate-400">
                      Өөр өдөр сонгох эсвэл “Бүх гүйлгээ” шүүлтүүрийг сонгоно уу.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-3">
                      <div className="tw-text-xs tw-font-semibold tw-text-slate-500">
                        {visibleRenewalLogs.length} гүйлгээ харагдаж байна
                      </div>
                      <div className="tw-text-xs tw-font-semibold tw-text-slate-400">
                        Өдрийн амжилттай орлого: {formatCurrency(extensionReportStats.totalAmount)}
                      </div>
                    </div>

                    <div className="tw-space-y-3 md:tw-hidden">
                      {visibleRenewalLogs.map((item) => {
                        const createdAt = toSafeDate(item.createdAt);
                        const status = String(item.status || "").trim().toLowerCase();
                        const isSuccess = status === "success";
                        const monthsExtended = Math.max(0, Number(item.monthsExtended) || 0);

                        return (
                          <article key={item.id} className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-4">
                            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                              <div>
                                <div className="tw-text-sm tw-font-semibold tw-tracking-wider tw-text-slate-900">
                                  {item.plate || "-"}
                                </div>
                                <div className="tw-mt-0.5 tw-text-xs tw-text-slate-400">
                                  {createdAt
                                    ? createdAt.toLocaleTimeString("mn-MN", { hour: "2-digit", minute: "2-digit" })
                                    : "-"}
                                </div>
                              </div>
                              <span
                                className={classNames(
                                  "tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold",
                                  isSuccess
                                    ? "tw-bg-emerald-50 tw-text-emerald-700"
                                    : "tw-bg-amber-50 tw-text-amber-700"
                                )}
                              >
                                {isSuccess ? "Амжилттай" : item.status || "Тодорхойгүй"}
                              </span>
                            </div>

                            <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-3">
                              <div>
                                <div className="tw-text-[11px] tw-text-slate-400">Сунгасан хугацаа</div>
                                <div className="tw-mt-0.5 tw-text-sm tw-font-semibold tw-text-slate-800">+{monthsExtended} сар</div>
                              </div>
                              <div className="tw-text-right">
                                <div className="tw-text-[11px] tw-text-slate-400">Төлбөр</div>
                                <div className="tw-mt-0.5 tw-text-sm tw-font-semibold tw-text-slate-900">{formatCurrency(item.amount)}</div>
                              </div>
                            </div>

                            <div className="tw-mt-3 tw-border-t tw-border-slate-100 tw-pt-3 tw-text-xs">
                              <div className="tw-flex tw-justify-between tw-gap-3">
                                <span className="tw-text-slate-400">Эх үүсвэр</span>
                                <span className="tw-font-medium tw-text-slate-700">{item.source || "-"}</span>
                              </div>
                              <div className="tw-mt-2 tw-flex tw-items-start tw-justify-between tw-gap-3">
                                <span className="tw-shrink-0 tw-text-slate-400">Нэхэмжлэх</span>
                                <span className="tw-min-w-0 tw-break-all tw-text-right tw-font-mono tw-text-[11px] tw-text-slate-500">{item.invoiceNumber || "-"}</span>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>

                    <div className="tw-hidden tw-overflow-x-auto tw-rounded-xl tw-border tw-border-slate-200 md:tw-block">
                      <table className="tw-w-full tw-min-w-[1180px] tw-border-collapse tw-bg-white tw-text-left">
                        <thead className="tw-bg-slate-50">
                          <tr>
                            {[
                              "Цаг",
                              "Улсын дугаар",
                              "Сунгасан хугацаа",
                              "Төлбөр",
                              "Нэхэмжлэх",
                              "Эх үүсвэр",
                              "Төлөв",
                              "User ID",
                            ].map((label) => (
                              <th
                                key={label}
                                className="tw-px-4 tw-py-3.5 tw-text-[11px] tw-font-semibold  tw-text-slate-500"
                              >
                                {label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-slate-100">
                          {visibleRenewalLogs.map((item) => {
                            const createdAt = toSafeDate(item.createdAt);
                            const status = String(item.status || "")
                              .trim()
                              .toLowerCase();
                            const isSuccess = status === "success";
                            const monthsExtended = Math.max(
                              0,
                              Number(item.monthsExtended) || 0
                            );

                            return (
                              <tr
                                key={item.id}
                                className="tw-transition hover:tw-bg-slate-50/80"
                              >
                                <td className="tw-px-4 tw-py-4">
                                  <div className="tw-text-xs tw-font-bold tw-text-slate-800">
                                    {createdAt
                                      ? createdAt.toLocaleTimeString("mn-MN", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })
                                      : "-"}
                                  </div>
                                </td>

                                <td className="tw-px-4 tw-py-4">
                                  <span className="tw-inline-flex tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-semibold tw-tracking-widest tw-text-slate-800">
                                    {item.plate || "-"}
                                  </span>
                                </td>

                                <td className="tw-px-4 tw-py-4">
                                  <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-slate-100 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-slate-700">
                                    <FiCalendar /> +{monthsExtended} сар
                                  </span>
                                </td>

                                <td className="tw-px-4 tw-py-4">
                                  <div className="tw-text-sm tw-font-semibold tw-text-slate-900">
                                    {formatCurrency(item.amount)}
                                  </div>
                                </td>

                                <td className="tw-px-4 tw-py-4">
                                  <div
                                    className="tw-max-w-[240px] tw-truncate tw-font-mono tw-text-[11px] tw-font-semibold tw-text-slate-500"
                                    title={item.invoiceNumber || ""}
                                  >
                                    {item.invoiceNumber || "-"}
                                  </div>
                                </td>

                                <td className="tw-px-4 tw-py-4">
                                  <span className="tw-rounded-lg tw-bg-slate-100 tw-px-2.5 tw-py-1.5 tw-text-[11px] tw-font-bold tw-text-slate-600">
                                    {item.source || "-"}
                                  </span>
                                </td>

                                <td className="tw-px-4 tw-py-4">
                                  <span
                                    className={classNames(
                                      "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold",
                                      isSuccess
                                        ? "tw-bg-emerald-50 tw-text-emerald-700"
                                        : "tw-bg-amber-50 tw-text-amber-700"
                                    )}
                                  >
                                    {isSuccess ? <FiCheck /> : <FiClock />}
                                    {isSuccess ? "Амжилттай" : item.status || "Тодорхойгүй"}
                                  </span>
                                </td>

                                <td className="tw-px-4 tw-py-4">
                                  <div
                                    className="tw-max-w-[220px] tw-truncate tw-font-mono tw-text-[11px] tw-font-semibold tw-text-slate-500"
                                    title={item.userId || ""}
                                  >
                                    {item.userId || "-"}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </Card>
            </div>
          ))}

        {activeTab === "users" &&
          (!selectedParkingId ? (
            <EmptyParking />
          ) : (
            <div className="tw-grid tw-grid-cols-1 tw-gap-5 lg:tw-grid-cols-[320px_minmax(0,1fr)]">
              <Card
                title="Хэрэглэгч"
                description={`${users.length} хэрэглэгч бүртгэлтэй`}
                icon={FiUsers}
              >
                <div className="tw-relative tw-mb-3">
                  <FiSearch className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" />
                  <input
                    value={userSearch}
                    onChange={(event) => setUserSearch(event.target.value)}
                    placeholder="Имэйлээр хайх"
                    className="tw-h-11 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-pl-10 tw-pr-3 tw-text-sm tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)] tw-outline-none tw-transition-all focus:tw-border-blue-400 focus:tw-ring-4 focus:tw-ring-blue-500/10"
                  />
                </div>

                {loadingUsers ? (
                  <div className="tw-flex tw-h-32 tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-slate-400">
                    <FiLoader className="tw-animate-spin" /> Уншиж байна...
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="tw-rounded-xl tw-bg-slate-50 tw-p-6 tw-text-center tw-text-sm tw-text-slate-400">
                    Хэрэглэгч олдсонгүй.
                  </div>
                ) : (
                  <div className="tw-max-h-[520px] tw-space-y-2 tw-overflow-y-auto">
                    {filteredUsers.map((user) => {
                      const active = selectedUserEmail === user.email;
                      return (
                        <button
                          key={user.email}
                          type="button"
                          onClick={() => handleUserSelect(user.email)}
                          className={classNames(
                            "tw-w-full tw-rounded-xl tw-border tw-p-3 tw-text-left tw-transition-all",
                            active
                              ? "tw-border-blue-400 tw-bg-blue-50/60 tw-ring-4 tw-ring-blue-500/10"
                              : "tw-border-slate-200 tw-bg-white hover:tw-border-blue-200 hover:tw-shadow-md hover:tw-shadow-slate-900/5"
                          )}
                        >
                          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                            <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800">
                              {user.email}
                            </span>
                            {user.isMaster === true && (
                              <FiStar className="tw-shrink-0 tw-text-amber-500" />
                            )}
                          </div>
                          <span className="tw-mt-1 tw-block tw-text-[11px] tw-text-slate-400">
                            {user.role || "user"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </Card>

              {!selectedUser ? (
                <div className="tw-flex tw-min-h-[300px] tw-flex-col tw-items-center tw-justify-center tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-300 tw-bg-white tw-p-6 tw-text-center tw-shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
                  <FiUsers size={28} className="tw-text-slate-300" />
                  <p className="tw-m-0 tw-mt-3 tw-text-sm tw-font-semibold tw-text-slate-500">
                    Тохируулах хэрэглэгчээ сонгоно уу.
                  </p>
                </div>
              ) : (
                <Card
                  title={selectedUser.email}
                  description="Хандах цэс болон Master эрх"
                  icon={FiShield}
                  action={
                    <button
                      type="button"
                      onClick={savePermissions}
                      disabled={savingPermissions || selectedUser.isMaster === true}
                      className="tw-flex tw-h-9 tw-items-center tw-gap-2 tw-rounded-xl tw-bg-blue-600 tw-px-3.5 tw-text-xs tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-blue-600/15 disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300 disabled:tw-shadow-none"
                    >
                      {savingPermissions ? (
                        <FiLoader className="tw-animate-spin" />
                      ) : (
                        <FiSave />
                      )}
                      Хадгалах
                    </button>
                  }
                >
                  {myRole === "superadmin" && (
                    <div className="tw-mb-5 tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
                      <div>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-amber-900">
                          <FiStar /> Master эрх
                        </div>
                        <p className="tw-m-0 tw-mt-1 tw-text-xs tw-leading-5 tw-text-amber-700">
                          Master хэрэглэгч тухайн зогсоолын бүх тохиргоонд хандана.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={toggleMaster}
                        disabled={savingPermissions}
                        className={classNames(
                          "tw-h-9 tw-rounded-xl tw-px-4 tw-text-xs tw-font-bold tw-transition",
                          selectedUser.isMaster === true
                            ? "tw-bg-amber-600 tw-text-white hover:tw-bg-amber-700"
                            : "tw-border tw-border-amber-300 tw-bg-white tw-text-amber-700 hover:tw-bg-amber-100"
                        )}
                      >
                        {selectedUser.isMaster === true ? "Master цуцлах" : "Master болгох"}
                      </button>
                    </div>
                  )}

                  {selectedUser.isMaster === true ? (
                    <div className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-100 tw-p-5 tw-text-sm tw-font-medium tw-leading-6 tw-text-slate-700">
                      Энэ хэрэглэгч Master эрхтэй тул бүх цэс рүү автоматаар хандана.
                    </div>
                  ) : (
                    <>
                      <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-3">
                        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-slate-700">
                          Зөвшөөрөгдөх цэс
                        </p>
                        <button
                          type="button"
                          onClick={toggleAllPermissions}
                          className="tw-text-xs tw-font-bold tw-text-slate-700 hover:tw-text-slate-700"
                        >
                          {allPermissionUrls.every((url) =>
                            userPermissions.includes(url)
                          )
                            ? "Бүгдийг цуцлах"
                            : "Бүгдийг сонгох"}
                        </button>
                      </div>

                      <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
                        {permissionItems.map((item) => {
                          const checked = userPermissions.includes(item.url);
                          return (
                            <label
                              key={item.url}
                              className={classNames(
                                "tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-p-3 tw-transition",
                                checked
                                  ? "tw-border-slate-300 tw-bg-slate-100"
                                  : "tw-border-slate-200 tw-bg-white hover:tw-border-slate-300"
                              )}
                            >
                              <input
                                type="checkbox"
                                className="tw-hidden"
                                checked={checked}
                                onChange={() => togglePermission(item.url)}
                              />
                              <span
                                className={classNames(
                                  "tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border",
                                  checked
                                    ? "tw-border-slate-900 tw-bg-slate-900 tw-text-white"
                                    : "tw-border-slate-300 tw-bg-white tw-text-transparent"
                                )}
                              >
                                <FiCheck size={13} />
                              </span>
                              <span className="tw-min-w-0">
                                <span className="tw-block tw-truncate tw-text-xs tw-font-semibold tw-text-slate-800">
                                  {item.label}
                                </span>
                                <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[10px] tw-text-slate-400">
                                  {item.group}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  )}
                </Card>
              )}
            </div>
          ))}
      </main>

      <Modal
        open={createOpen}
        title="Шинэ зогсоол үүсгэх"
        description="Үндсэн мэдээлэл, үнэ болон орох/гарах камерын тоог тохируулна."
        onClose={closeCreateModal}
      >
        <form onSubmit={createParking}>
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 tw-p-5 md:tw-grid-cols-2">
            <Field
              label="Зогсоолын ID *"
              name="parkingId"
              value={createForm.parkingId}
              onChange={handleCreateFormChange}
              placeholder="ulziit-parking"
              hint="Firestore document ID. Дараа нь солихгүй байхыг зөвлөж байна."
              disabled={creating}
            />
            <Field
              label="Зогсоолын нэр *"
              name="name"
              value={createForm.name}
              onChange={handleCreateFormChange}
              placeholder="Өлзийт СӨХ"
              disabled={creating}
            />
            <Field
              label="Join Code *"
              name="joinCode"
              value={createForm.joinCode}
              onChange={handleCreateFormChange}
              placeholder="515151"
              disabled={creating}
            />
            <Field
              label="Хаяг"
              name="address"
              value={createForm.address}
              onChange={handleCreateFormChange}
              placeholder="ХУД, 4-р хороо"
              disabled={creating}
            />
            <Field
              label="Үнэгүй минут"
              name="freeMinutes"
              type="number"
              value={createForm.freeMinutes}
              onChange={handleCreateFormChange}
              disabled={creating}
            />
            <Field
              label="Урилгатай зочны үнэгүй минут"
              name="invitedFreeMinutes"
              type="number"
              value={createForm.invitedFreeMinutes}
              onChange={handleCreateFormChange}
              hint="Жишээ: 240 = 4 цаг."
              disabled={creating}
            />
            <Field
              label="Цагийн үнэ"
              name="hourlyPrice"
              type="number"
              value={createForm.hourlyPrice}
              onChange={handleCreateFormChange}
              disabled={creating}
            />
            <Field
              label="Том машины цагийн үнэ"
              name="largeVehiclePrice"
              type="number"
              value={createForm.largeVehiclePrice}
              onChange={handleCreateFormChange}
              disabled={creating}
            />
            <Field
              label="Том машины тогтмол үнэ"
              name="fixedLargeVehiclePrice"
              type="number"
              value={createForm.fixedLargeVehiclePrice}
              onChange={handleCreateFormChange}
              disabled={creating}
            />

            <div className="md:tw-col-span-2 tw-rounded-2xl tw-border tw-border-blue-100 tw-bg-blue-50/60 tw-p-4">
              <div className="tw-mb-3">
                <h3 className="tw-m-0 tw-text-sm tw-font-bold tw-text-slate-900">
                  Камерын тоо
                </h3>
                <p className="tw-m-0 tw-mt-1 tw-text-xs tw-leading-5 tw-text-slate-500">
                  Шинэ зогсоол үүсэхэд enter_1, enter_2, exit_1... camera ID болон Dashboard товчнууд автоматаар бэлтгэгдэнэ.
                </p>
              </div>

              <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                <Field
                  label="Орох камерын тоо"
                  name="enterCameraCount"
                  type="number"
                  value={createForm.enterCameraCount}
                  onChange={handleCreateFormChange}
                  hint="0–32"
                  disabled={creating}
                />

                <Field
                  label="Гарах камерын тоо"
                  name="exitCameraCount"
                  type="number"
                  value={createForm.exitCameraCount}
                  onChange={handleCreateFormChange}
                  hint="0–32"
                  disabled={creating}
                />
              </div>

              <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
                <span className="tw-inline-flex tw-rounded-full tw-bg-emerald-100 tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-emerald-700">
                  ENTER: {Number(createForm.enterCameraCount || 0)}
                </span>
                <span className="tw-inline-flex tw-rounded-full tw-bg-blue-100 tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-blue-700">
                  EXIT: {Number(createForm.exitCameraCount || 0)}
                </span>
                <span className="tw-inline-flex tw-rounded-full tw-bg-white tw-px-3 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-slate-600 tw-ring-1 tw-ring-slate-200">
                  Нийт: {
                    Number(createForm.enterCameraCount || 0) +
                    Number(createForm.exitCameraCount || 0)
                  }
                </span>
              </div>
            </div>

            <div className="md:tw-col-span-2">
              <Field
                label="Анхны Master хэрэглэгчийн имэйл"
                name="managerEmail"
                type="email"
                value={createForm.managerEmail}
                onChange={handleCreateFormChange}
                placeholder="admin@example.com"
                hint="Заавал биш. Firestore эрх үүсгэнэ, харин Firebase Authentication хэрэглэгчийг тусад нь бүртгэнэ."
                disabled={creating}
                autoComplete="email"
              />
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-border-t tw-border-slate-100 tw-bg-slate-50 tw-px-5 tw-py-4">
            <button
              type="button"
              onClick={closeCreateModal}
              disabled={creating}
              className="tw-h-10 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-text-sm tw-font-semibold tw-text-slate-600 tw-transition hover:tw-bg-slate-100 disabled:tw-opacity-50"
            >
              Болих
            </button>
            <button
              type="submit"
              disabled={creating}
              className="tw-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-lg tw-bg-slate-900 tw-px-4 tw-text-sm tw-font-bold tw-text-white tw-transition hover:tw-bg-slate-800 disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300"
            >
              {creating ? (
                <FiLoader className="tw-animate-spin" />
              ) : (
                <FiPlus />
              )}
              {creating ? "Үүсгэж байна..." : "Зогсоол үүсгэх"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}